import { createScopedErrors, registerCodes } from '@norkron/web-errors';
import { registerResponsiveContract, withConformance } from '@norkron/web-conformance';
import { registerLazy } from '@norkron/web-conformance/register-lazy';
import { getCssVar } from '@norkron/web-themer';
import { privzServedOps, privzRouteFor, loadPrivileges } from '@norkron/web-privileges';
import { createContext, useState, useEffect, useMemo, useContext, useId, useReducer, useRef, useCallback } from 'react';
import { z } from 'zod';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { DataPanel, BadgePill, ViewContainer } from '@norkron/web-admin-shell';
import { useLiveAnnouncer } from '@norkron/web-a11y';
import { DefaultErrorFallback } from '@norkron/web-error-boundary';
import { Tabs, TabPanels, TabPanel } from '@norkron/web-tabs';
import { formatNumber, formatPercent } from '@norkron/web-numeric-format';
import { EmptyState, LoadingState } from '@norkron/web-states';
import { RichTooltip } from '@norkron/web-tooltip';

// src/error-codes.ts

// src/version.ts
var LIB_VERSION = "0.3.0";
var WEB_PRIVILEGES_ADMIN_LIB = "@norkron/web-privileges-admin";

// src/error-codes.ts
var PRIVZ_ADMIN_ERRORS = {
  /** A product name reached a structural identifier. */
  GENERICITY_VIOLATION: "PA_GENERICITY_VIOLATION",
  /** An outbound payload carried a client-computed authorization verdict. */
  VERDICT_LEAK: "PA_VERDICT_LEAK",
  /** A call was made to an operation the server does not implement. */
  UNSERVED_OPERATION: "PA_UNSERVED_OPERATION",
  /** A component was rendered outside the provider. */
  MISSING_PROVIDER: "PA_MISSING_PROVIDER",
  /** The design tokens this package paints with did not resolve. */
  TOKEN_CONTRACT_BROKEN: "PA_TOKEN_CONTRACT_BROKEN"
};
try {
  createScopedErrors(WEB_PRIVILEGES_ADMIN_LIB, /* @__PURE__ */ Symbol.for("norkron.lib.privileges-admin"));
} catch {
}
try {
  registerCodes(WEB_PRIVILEGES_ADMIN_LIB, {
    PA_GENERICITY_VIOLATION: {
      description: "A product name reached a STRUCTURAL identifier \u2014 a capability key, a group id, or any token that becomes a data-testid or DOM id and therefore contract surface. Presentation copy is unrestricted; identifiers are not, because a product word baked into an id makes this package non-generic for every other product.",
      severity: "error",
      docsAnchor: "privileges-admin/genericity-violation",
      exampleFix: 'Keep the identifier generic and put the product word in the LABEL instead: { "doc.export": { label: "Export a Curate document", group: "documents" } } \u2014 never a group id or capability key containing the product name.'
    },
    PA_VERDICT_LEAK: {
      description: "An outbound payload carried a client-computed authorization verdict (a key like `verdict`, `allowed`, `entries` or `resolved_capabilities`). The request was REFUSED before it left the browser. The browser resolves capabilities only to EXPLAIN and PREVIEW; the server re-resolves and enforces on every request, and no endpoint accepts a verdict computed here.",
      severity: "error",
      docsAnchor: "privileges-admin/verdict-leak",
      exampleFix: "Send the INPUTS the server needs, not the answer you computed: post the grant/subject/scope and let the server resolve. If the server genuinely needs this data it needs a ROUTE, not a request field."
    },
    PA_UNSERVED_OPERATION: {
      description: "A call was attempted against an operation the mount does not serve. `served` is read from the route manifest postgresz-privileges emits from its comptime route table, so this means the operation name is genuinely absent from this mount \u2014 either the server compiled the route out with a different PrivOpts, or this console names an operation upstream retired.",
      severity: "error",
      docsAnchor: "privileges-admin/unserved-operation",
      exampleFix: 'Check the manifest before changing anything: privzRouteFor("console", "<op>") from @norkron/web-privileges. If the route exists but the mount lacks it, the BFF needs enable_ops_console (or enable_groups) turned on.'
    },
    PA_MISSING_PROVIDER: {
      description: "A component was rendered outside <PrivilegesAdminProvider>. The shell renders a module page as a PROPLESS ComponentType and mounts no transport provider, so this package closes over its dependencies at module construction and delivers them through its own context.",
      severity: "error",
      docsAnchor: "privileges-admin/missing-provider",
      exampleFix: "Build the module with createPrivilegesAdminModule(deps) or fillPrivilegesAdminSeat(persona, deps) and register it with buildConsole \u2014 or wrap the subtree in <PrivilegesAdminProvider deps={\u2026}> yourself."
    },
    PA_TOKEN_CONTRACT_BROKEN: {
      description: "A design token this package paints with resolved to empty. Anything this package PORTALS (a tooltip, a menu) escapes CSS custom-property inheritance and lands under document.body, where a var(--x, transparent) fallback silently paints invisible text \u2014 so the tokens are declared on :root and verified rather than assumed.",
      severity: "warning",
      docsAnchor: "privileges-admin/token-contract-broken",
      exampleFix: 'Import the stylesheet once at app entry: import "@norkron/web-privileges-admin/styles.css". If a host theme overrides these tokens, declare them on :root, not on a subtree the portal escapes.'
    }
  });
} catch {
}
var RESPONSIVE_CONTRACT = {
  lib: WEB_PRIVILEGES_ADMIN_LIB,
  supportedBreakpoints: ["xs", "sm", "md", "lg", "xl", "2xl", "3xl"],
  breakpointBehavior: {
    xs: "compact",
    sm: "compact",
    md: "full",
    lg: "full",
    xl: "full",
    "2xl": "full",
    "3xl": "full"
  },
  containerQueries: [".privz-admin__grid-scroll"],
  rwdSelfCheckCodes: []
};
try {
  registerResponsiveContract(RESPONSIVE_CONTRACT.lib, RESPONSIVE_CONTRACT);
} catch {
}

// src/availability.ts
var AVAILABLE = Object.freeze({ state: "available", disabled: false });
function unavailable(reason) {
  return { state: "unavailable", reason, disabled: true };
}
function unsupported(reason, blockedBy) {
  return { state: "unsupported", reason, blockedBy, disabled: true };
}
function availabilityOf(op2, gate) {
  if (!op2.served) {
    return unsupported(
      op2.blockedReason ?? `The server does not implement "${op2.op}" yet.`,
      op2.blockedBy
    );
  }
  return gate ?? AVAILABLE;
}
function requiresCapability(held, capability, what) {
  if (held === void 0) {
    return unavailable(
      `No resolved capability set was handed to this console, so ${what} is held back until the server says who you are.`
    );
  }
  if (!held.has(capability)) {
    return unavailable(
      `You do not hold "${capability}", which the server requires for ${what}. Ask an administrator to grant it.`
    );
  }
  return AVAILABLE;
}
function fromCanGrant(result) {
  if (result.allowed) return AVAILABLE;
  return unavailable(
    `The server's grant ceiling would refuse this: ${result.reason ?? "no reason supplied"}. This is a local pre-check of the same rule the server applies \u2014 it is a courtesy, not the decision.`
  );
}
var INTERACTIVE = 'button, input, select, textarea, [role="button"], [role="switch"], [role="checkbox"]';
function findInertControls(root) {
  const findings = [];
  for (const el of Array.from(root.querySelectorAll(INTERACTIVE))) {
    const testid = el.getAttribute("data-testid") ?? el.tagName.toLowerCase();
    const disabled = el.hasAttribute("disabled") || el.getAttribute("aria-disabled") === "true";
    if (disabled) {
      const reason = el.getAttribute("data-unavailable-reason") ?? el.getAttribute("title") ?? (el.getAttribute("aria-describedby") ? root.querySelector(`#${el.getAttribute("aria-describedby")}`)?.textContent ?? null : null);
      if (reason === null || reason.trim().length === 0) {
        findings.push({
          testid,
          problem: "disabled-without-reason",
          detail: "Disabled with no accessible explanation of why, or of what would change it."
        });
      }
      continue;
    }
    if (el.getAttribute("data-wired") !== "true" && el.getAttribute("type") !== "text") {
      findings.push({
        testid,
        problem: "enabled-and-inert",
        detail: "Enabled but not marked as wired to a handler \u2014 it may render as actionable and do nothing."
      });
    }
  }
  return findings;
}

// src/self-check.ts
var REQUIRED_TOKENS = Object.freeze([
  "--norkron-color-bg-base",
  "--norkron-color-fg-base",
  "--norkron-color-fg-muted",
  "--norkron-color-border",
  "--norkron-state-danger",
  "--norkron-state-success",
  "--norkron-state-warning",
  "--norkron-space-2",
  "--norkron-space-3",
  "--norkron-radius-md",
  "--norkron-font-size-sm"
]);
function finding(code, severity, message, selector) {
  return { code, severity, message, lib: WEB_PRIVILEGES_ADMIN_LIB, version: LIB_VERSION, selector };
}
function selfCheck(root, opts = {}) {
  const max = opts.maxFindings ?? 100;
  const out = [];
  try {
    const scope = root.closest(".privz-admin") ?? root;
    const styles = globalThis.getComputedStyle?.(scope);
    if (styles) {
      for (const token of REQUIRED_TOKENS) {
        if (out.length >= max) break;
        const viaThemer = (getCssVar(token) ?? "").trim();
        const viaSubtree = styles.getPropertyValue(token).trim();
        if (viaThemer.length === 0 && viaSubtree.length === 0) {
          out.push(
            finding(
              "PA_TOKEN_CONTRACT_BROKEN",
              "critical",
              `The design token "${token}" resolved to nothing on this subtree. Every colour in this package falls back through var(\u2026, transparent), so an unresolved token paints invisible text rather than failing loudly. Import "@norkron/web-privileges-admin/styles.css" at app entry, and if this element is portalled make sure the tokens are declared on :root rather than on an ancestor the portal escaped.`,
              ".privz-admin"
            )
          );
        }
      }
    }
    for (const f of findInertControls(root)) {
      if (out.length >= max) break;
      out.push(
        finding(
          f.problem === "enabled-and-inert" ? "PA_CONTROL_INERT" : "PA_CONTROL_UNEXPLAINED",
          f.problem === "enabled-and-inert" ? "critical" : "major",
          `${f.detail} (control: ${f.testid})`,
          `[data-testid="${f.testid}"]`
        )
      );
    }
  } catch {
  }
  return out;
}

// src/genericity.ts
var PRODUCT_TOKENS_STRICT = Object.freeze([
  "aether",
  "affili8",
  "cerebr8",
  "fullstack-audit",
  "illuminat8",
  "illuminate",
  "investigate",
  "litmus",
  "memoria",
  "move-planner",
  "mydiet",
  "ophanim",
  "orchestrate",
  "procedural-calendar",
  "siteprobe",
  "vibegrade",
  "wettformat",
  "zenith"
]);
var PRODUCT_TOKENS_AMBIGUOUS = Object.freeze([
  "affiliate",
  "curate",
  "elaborate",
  "integrate",
  "narrate",
  "simulate"
]);
var PRODUCT_TOKENS = Object.freeze([
  ...PRODUCT_TOKENS_STRICT,
  ...PRODUCT_TOKENS_AMBIGUOUS
]);
var ALPHANUMERIC = /[A-Za-z0-9]/;
function isWholeWord(haystack, at, len) {
  const before = at === 0 ? "" : haystack[at - 1];
  const after = haystack[at + len] ?? "";
  if (before && ALPHANUMERIC.test(before)) return false;
  if (after && ALPHANUMERIC.test(after)) return false;
  return true;
}
function isProductShaped(haystack, at, len) {
  const before = haystack.slice(Math.max(0, at - 12), at);
  const after = haystack.slice(at + len, at + len + 8);
  if (/@norkron\/$/.test(before)) return true;
  if (/products\/$/.test(before)) return true;
  if (/^-(ops|admin|module|console|backend|frontend)\b/.test(after)) return true;
  const quoteBefore = before.slice(-1);
  const quoteAfter = after.slice(0, 1);
  if ((quoteBefore === "'" || quoteBefore === '"') && quoteBefore === quoteAfter) {
    return true;
  }
  return false;
}
function excerptAt(text, at) {
  const lineStart = text.lastIndexOf("\n", at) + 1;
  const lineEndRaw = text.indexOf("\n", at);
  const lineEnd = lineEndRaw === -1 ? text.length : lineEndRaw;
  return text.slice(lineStart, lineEnd).trim().slice(0, 200);
}
function scan(text, where, tokens, rule, out) {
  const haystack = text.toLowerCase();
  for (const token of tokens) {
    let from = 0;
    for (; ; ) {
      const at = haystack.indexOf(token, from);
      if (at === -1) break;
      from = at + token.length;
      if (!isWholeWord(haystack, at, token.length)) continue;
      if (rule === "product-context" && !isProductShaped(haystack, at, token.length)) continue;
      out.push({ where, token, rule, excerpt: excerptAt(text, at) });
    }
  }
}
function findProductTokens(text, where) {
  const out = [];
  scan(text, where, PRODUCT_TOKENS_STRICT, "strict", out);
  scan(text, where, PRODUCT_TOKENS_AMBIGUOUS, "product-context", out);
  return out;
}
var GenericityViolationError = class extends Error {
  constructor(violations) {
    super(
      `@norkron/web-privileges-admin is product-agnostic by contract, and ${violations.length} product-specific string reached its structural surface: ` + violations.map((v) => `${v.token} @ ${v.where} ("${v.excerpt}")`).join("; ") + ". A product injects its vocabulary as DATA \u2014 display overrides keyed by capability string \u2014 it never names itself inside this package."
    );
    this.violations = violations;
    this.name = "GenericityViolationError";
  }
  violations;
};
function assertGenericIdentifiers(labelled) {
  const violations = [];
  for (const [where, values] of Object.entries(labelled)) {
    for (const v of values) violations.push(...findProductTokens(v, where));
  }
  if (violations.length > 0) throw new GenericityViolationError(violations);
}
var CONSOLE_PROFILE = "console";
function op(name) {
  const route = privzRouteFor(CONSOLE_PROFILE, name);
  if (route !== void 0) {
    return { op: name, kind: route.kind, served: true, route };
  }
  return {
    op: name,
    kind: null,
    served: false,
    blockedBy: `postgresz-privileges route manifest (profile "${CONSOLE_PROFILE}")`,
    blockedReason: `The route manifest published by @norkron/web-privileges does not carry "${name}" on the "${CONSOLE_PROFILE}" mount, so calling it could only 404. Either the server compiled the route out with a different PrivOpts, or this console names an operation that upstream retired \u2014 check the manifest before changing anything here.`
  };
}
var OPS = {
  // ── grants ──────────────────────────────────────────────────────────────
  grantsCreate: op("privileges.grants.create"),
  grantsRevoke: op("privileges.grants.revoke"),
  grantsList: op("privileges.grants.list"),
  // ── bundles ─────────────────────────────────────────────────────────────
  bundlesList: op("privileges.bundles.list"),
  bundlesDefine: op("privileges.bundles.define"),
  bundlesSetCapability: op("privileges.bundles.setCapability"),
  bundlesRemoveCapability: op("privileges.bundles.removeCapability"),
  // ── capability vocabulary (server data — the product's own words) ───────
  capabilitiesList: op("privileges.capabilities.list"),
  capabilitiesRegister: op("privileges.capabilities.register"),
  // ── groups + membership ─────────────────────────────────────────────────
  groupsCreate: op("privileges.groups.create"),
  groupsList: op("privileges.groups.list"),
  groupsAddMember: op("privileges.groups.addMember"),
  groupsRemoveMember: op("privileges.groups.removeMember"),
  groupsMembers: op("privileges.groups.members"),
  groupsArchive: op("privileges.groups.archive"),
  // ── resolution (server authority) ───────────────────────────────────────
  resolve: op("privileges.resolve"),
  /**
   * The F7 route that makes the shipped `privz.wasm` usable: it projects a
   * `PolicySnapshot`, which every wasm verb takes as its first argument.
   *
   * This is the op the old hand-written table marked `served: false`. It has
   * been served since v0.8.0, and `context.tsx` now defaults `snapshotSource`
   * to it, so per-capability provenance works with zero host wiring.
   */
  snapshotGet: op("privileges.snapshot.get"),
  // ── ops console — read ──────────────────────────────────────────────────
  rolesMatrix: op("ops.platform.roles.matrix"),
  rolesEdit: op("ops.platform.roles.edit"),
  policySimulate: op("ops.platform.policy.simulate"),
  elevationBoard: op("ops.platform.elevation.board"),
  elevationApprove: op("ops.platform.elevation.approve"),
  accessReviewsList: op("ops.platform.access_reviews.list"),
  // ── ops console — the v0.8.0 governance WRITES ──────────────────────────
  //
  // Before v0.8.0 an elevation could be APPROVED but not asked for, a campaign
  // existed only if a migration seeded it, and `sod_pair` had a reader and no
  // writer. All three halves landed; naming them here is what lets the panels
  // complete a lifecycle instead of offering create-without-close and
  // add-without-remove.
  elevationRequest: op("ops.platform.elevation.request"),
  accessReviewCreate: op("ops.platform.access_reviews.create"),
  accessReviewDecide: op("ops.platform.access_reviews.decide"),
  accessReviewClose: op("ops.platform.access_reviews.close"),
  sodPairList: op("ops.platform.sod_pairs.list"),
  sodPairCreate: op("ops.platform.sod_pairs.create"),
  sodPairRemove: op("ops.platform.sod_pairs.remove")
};
var ALL_OPS = Object.freeze(Object.values(OPS));
var SERVED_OP_NAMES = Object.freeze(
  ALL_OPS.filter((d) => d.served).map((d) => d.op).sort()
);
var MOUNT_OP_NAMES = privzServedOps(CONSOLE_PROFILE);
var DELIBERATELY_EXCLUDED_OPS = Object.freeze({});
var PARAM_DENYLIST = Object.freeze([
  "allowed",
  "authorized",
  "decision",
  "entries",
  "granted_capabilities",
  "permitted",
  "privilege_set",
  "resolved",
  "resolved_capabilities",
  "verdict"
]);
var UnservedOperationError = class extends Error {
  constructor(op2, blockedBy, reason) {
    super(
      `@norkron/web-privileges-admin: "${op2}" is not implemented by the server (${blockedBy ?? "no feature request recorded"}). ${reason} This call was refused locally rather than sent, so no request could appear to succeed.`
    );
    this.op = op2;
    this.blockedBy = blockedBy;
    this.name = "UnservedOperationError";
  }
  op;
  blockedBy;
};
function assertServed(d) {
  if (!d.served || d.kind === null) {
    throw new UnservedOperationError(d.op, d.blockedBy, d.blockedReason ?? "");
  }
  return d.kind;
}
var DENYLIST_EXCEPTIONS = Object.freeze({
  "ops.platform.access_reviews.decide": Object.freeze(["decision"])
});
function isDenylistException(opName, key, depth) {
  return depth === 0 && (DENYLIST_EXCEPTIONS[opName]?.includes(key) ?? false);
}
var VerdictLeakError = class extends Error {
  constructor(op2, key) {
    super(
      `@norkron/web-privileges-admin: refused to send "${key}" to "${op2}" \u2014 a client-computed authorization verdict may never travel to the server. The server re-resolves on every request; if this data is genuinely needed server-side it needs a route, not a request field.`
    );
    this.op = op2;
    this.key = key;
    this.name = "VerdictLeakError";
  }
  op;
  key;
};
function assertNoVerdict(opName, params, depth = 0) {
  if (depth > 8 || params === null || typeof params !== "object") return;
  if (Array.isArray(params)) {
    for (const v of params) assertNoVerdict(opName, v, depth + 1);
    return;
  }
  for (const [k, v] of Object.entries(params)) {
    if (PARAM_DENYLIST.includes(k) && !isDenylistException(opName, k, depth)) {
      throw new VerdictLeakError(opName, k);
    }
    assertNoVerdict(opName, v, depth + 1);
  }
}
var effect = z.string();
var grantViewSchema = z.object({
  id: z.string(),
  subject_kind: z.string(),
  subject_id: z.string(),
  scope_kind: z.string(),
  scope_id: z.string(),
  capability: z.string().optional(),
  bundle_key: z.string().optional(),
  effect,
  source: z.string(),
  conditions_json: z.string(),
  granted_at_unix: z.number(),
  idempotent: z.boolean().optional()
});
var bundleViewSchema = z.object({
  id: z.string(),
  key: z.string(),
  scope_kind: z.string(),
  rank: z.number(),
  segment: z.string().optional()
});
var capabilityViewSchema = z.object({
  key: z.string(),
  namespace: z.string(),
  display_name: z.string(),
  deprecated: z.boolean().optional()
});
var groupViewSchema = z.object({
  id: z.string(),
  account_id: z.string(),
  key: z.string(),
  display_name: z.string()
});
var groupMemberViewSchema = z.object({
  id: z.string(),
  group_id: z.string(),
  person_id: z.string(),
  added_at_unix: z.number()
});
var privilegeSetViewSchema = z.object({
  entries: z.array(z.object({ capability: z.string(), source: z.string() })),
  /** `/v1/*` OMITS null optionals; `bundle_role` is absent off workspace scopes. */
  bundle_role: z.string().nullable().optional()
});
var roleBundleViewSchema = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  description: z.string(),
  capability_atoms: z.array(z.string()),
  member_count: z.number(),
  editable: z.boolean()
});
var roleMatrixViewSchema = z.object({
  bundles: z.array(roleBundleViewSchema),
  capabilities: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      category: z.string(),
      group: z.string(),
      group_label: z.string(),
      description: z.string(),
      sensitive: z.boolean()
    })
  ),
  matrix: z.array(
    z.object({
      bundle_id: z.string(),
      capability_key: z.string(),
      granted: z.boolean(),
      /**
       * ⚠️ REQUIRED HERE, not merely mirrored in the type. A `z.object` STRIPS keys
       * it does not declare, so a `PermissionMatrixCellView.is_default` that was
       * only added to the TypeScript interface would be validated away on the way
       * in — the field would arrive from the server, vanish at the boundary, and
       * the provenance marker would never render for any reason a reader of the
       * component could see.
       */
      is_default: z.boolean().optional()
    })
  )
});
var policyResultViewSchema = z.object({
  query: z.string(),
  allowed: z.array(z.string()),
  denied: z.array(z.string()),
  sod_conflicts: z.array(z.string())
});
var elevationGrantViewSchema = z.object({
  id: z.string(),
  staff_id: z.string(),
  staff_label: z.string(),
  capability: z.string(),
  kind: z.string(),
  status: z.string(),
  reason: z.string(),
  ticket_ref: z.string().nullable(),
  requested_at: z.string(),
  expires_at: z.string().nullable(),
  approver_id: z.string().nullable(),
  approver_label: z.string().nullable(),
  approved_at: z.string().nullable()
});
var elevationBoardViewSchema = z.object({
  active: z.array(elevationGrantViewSchema),
  pending: z.array(elevationGrantViewSchema)
});
var accessReviewItemViewSchema = z.object({
  staff_label: z.string(),
  capability: z.string(),
  decision: z.string(),
  reviewer_label: z.string().nullable()
});
var accessReviewCampaignViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  scope: z.string(),
  status: z.string(),
  period: z.string(),
  due_at: z.string(),
  total_items: z.number(),
  attested_count: z.number(),
  revoked_count: z.number(),
  reviewers: z.array(z.string()),
  items: z.array(accessReviewItemViewSchema)
});
var okViewSchema = z.object({ ok: z.boolean() });
var bundleCapViewSchema = z.object({
  bundle_id: z.string(),
  capability: z.string(),
  effect
});
var snapshotEffect = z.enum(["allow", "deny"]);
var policySnapshotSchema = z.object({
  snapshot_version: z.number().optional(),
  home_account_id: z.string().optional(),
  account_role: z.string().optional(),
  admins_manage_all: z.boolean().optional(),
  workspace: z.object({
    owner_account_id: z.string().optional(),
    managed_by_account_id: z.string().nullable().optional(),
    membership_role: z.string().nullable().optional(),
    membership_revoked_at_unix: z.number().nullable().optional()
  }).nullable().optional(),
  subjects: z.array(
    z.object({
      kind: z.string().optional(),
      grants: z.array(
        z.object({
          capability: z.string().nullable().optional(),
          bundle_id: z.string().nullable().optional(),
          effect: snapshotEffect.optional(),
          conditions_json: z.string().optional(),
          source: z.string()
        })
      ).optional()
    })
  ).optional(),
  bundles: z.array(
    z.object({
      id: z.string().optional(),
      key: z.string(),
      scope_kind: z.string(),
      capabilities: z.array(z.object({ capability: z.string(), effect: snapshotEffect.optional() })).optional()
    })
  ).optional()
});
var sodPairViewSchema = z.object({
  capability_a: z.string(),
  capability_b: z.string()
});
var ContractViolationError = class extends Error {
  constructor(op2, issues) {
    super(
      `@norkron/web-privileges-admin: the response to "${op2}" does not match the published contract \u2014 ` + issues.map((i) => `${i.path}: ${i.message}`).join("; ") + ". This console refuses to render a partial answer about somebody\u2019s access; the server view and this package have drifted and one of them needs updating."
    );
    this.op = op2;
    this.issues = issues;
    this.name = "ContractViolationError";
  }
  op;
  issues;
};
function parseResponse(op2, schema, value) {
  const result = schema.safeParse(value);
  if (result.success) return result.data;
  throw new ContractViolationError(
    op2,
    result.error.issues.map((i) => ({ path: i.path.join(".") || "(root)", message: i.message }))
  );
}

// src/client.ts
function call(bff, d, params) {
  const kind = assertServed(d);
  assertNoVerdict(d.op, params);
  return kind === "query" ? bff.query(d.op, params) : bff.mutate(d.op, params);
}
async function callChecked(bff, d, params, schema) {
  return parseResponse(d.op, schema, await call(bff, d, params));
}
function createPrivilegesClient(bff) {
  return {
    listCapabilities: async (namespace) => (await callChecked(
      bff,
      OPS.capabilitiesList,
      namespace ? { namespace } : {},
      z.object({ capabilities: z.array(capabilityViewSchema) })
    )).capabilities,
    listBundles: async (scopeKind) => (await callChecked(
      bff,
      OPS.bundlesList,
      scopeKind ? { scope_kind: scopeKind } : {},
      z.object({ bundles: z.array(bundleViewSchema) })
    )).bundles,
    defineBundle: (body) => callChecked(bff, OPS.bundlesDefine, body, bundleViewSchema),
    setBundleCapability: (bundleId, capability, effect2) => callChecked(
      bff,
      OPS.bundlesSetCapability,
      { id: bundleId, capability, effect: effect2 },
      bundleCapViewSchema
    ),
    removeBundleCapability: (bundleId, capability) => callChecked(
      bff,
      OPS.bundlesRemoveCapability,
      { id: bundleId, cap: capability },
      okViewSchema
    ),
    listGroups: async (accountId) => (await callChecked(
      bff,
      OPS.groupsList,
      { account_id: accountId },
      z.object({ groups: z.array(groupViewSchema) })
    )).groups,
    createGroup: (body) => callChecked(bff, OPS.groupsCreate, body, groupViewSchema),
    archiveGroup: (groupId) => callChecked(bff, OPS.groupsArchive, { id: groupId }, okViewSchema),
    listGroupMembers: async (groupId) => (await callChecked(
      bff,
      OPS.groupsMembers,
      { id: groupId },
      z.object({ members: z.array(groupMemberViewSchema) })
    )).members,
    addGroupMember: (groupId, personId) => callChecked(
      bff,
      OPS.groupsAddMember,
      { id: groupId, person_id: personId },
      groupMemberViewSchema
    ),
    removeGroupMember: (groupId, personId) => callChecked(bff, OPS.groupsRemoveMember, { id: groupId, person: personId }, okViewSchema),
    listGrantsForScope: async (scope) => (await callChecked(bff, OPS.grantsList, scope, z.object({ grants: z.array(grantViewSchema) }))).grants,
    listGrantsForSubject: async (subjectKind, subjectId) => (await callChecked(
      bff,
      OPS.grantsList,
      { subject_kind: subjectKind, subject_id: subjectId },
      z.object({ grants: z.array(grantViewSchema) })
    )).grants,
    createGrant: (body) => callChecked(bff, OPS.grantsCreate, body, grantViewSchema),
    revokeGrant: (grantId) => callChecked(bff, OPS.grantsRevoke, { id: grantId }, okViewSchema),
    resolve: (scope) => callChecked(bff, OPS.resolve, scope, privilegeSetViewSchema),
    rolesMatrix: () => callChecked(bff, OPS.rolesMatrix, {}, roleMatrixViewSchema),
    editRoleBundle: (bundleId, capabilityAtoms) => callChecked(
      bff,
      OPS.rolesEdit,
      { id: bundleId, capability_atoms: capabilityAtoms },
      roleBundleViewSchema
    ),
    simulatePolicy: (query) => callChecked(bff, OPS.policySimulate, { query }, policyResultViewSchema),
    elevationBoard: () => callChecked(bff, OPS.elevationBoard, {}, elevationBoardViewSchema),
    approveElevation: (elevationId, approverId) => callChecked(
      bff,
      OPS.elevationApprove,
      { id: elevationId, ...approverId ? { approver_id: approverId } : {} },
      elevationGrantViewSchema
    ),
    listAccessReviewCampaigns: () => callChecked(
      bff,
      OPS.accessReviewsList,
      {},
      z.array(accessReviewCampaignViewSchema)
    ),
    // ── the F7 snapshot: the input every wasm verb takes first ──────────
    //
    // ⛔ Validated at the boundary like every other response, and for a sharper
    // reason than usual: `/resolve` returns a FLATTENED entry list and
    // `/snapshot` returns the resolver's own input. They are different shapes
    // with overlapping vocabulary, and feeding the wrong one to the resolver
    // would produce a plausible, completely wrong answer about someone's
    // access rather than an error.
    snapshot: (scope, personId) => callChecked(
      bff,
      OPS.snapshotGet,
      personId !== void 0 && personId.length > 0 ? { ...scope, person_id: personId } : { ...scope },
      policySnapshotSchema
    ),
    // ── the v0.8.0 governance writes ────────────────────────────────────
    requestElevation: (body) => callChecked(bff, OPS.elevationRequest, body, elevationGrantViewSchema),
    createAccessReviewCampaign: (body) => callChecked(bff, OPS.accessReviewCreate, body, accessReviewCampaignViewSchema),
    decideAccessReviewItem: (campaignId, item) => callChecked(
      bff,
      OPS.accessReviewDecide,
      { id: campaignId, ...item },
      accessReviewItemViewSchema
    ),
    closeAccessReviewCampaign: (campaignId) => callChecked(
      bff,
      OPS.accessReviewClose,
      { id: campaignId },
      accessReviewCampaignViewSchema
    ),
    listSodPairs: async () => (await callChecked(
      bff,
      OPS.sodPairList,
      {},
      z.object({ pairs: z.array(sodPairViewSchema) })
    )).pairs,
    createSodPair: (pair) => callChecked(bff, OPS.sodPairCreate, pair, sodPairViewSchema),
    removeSodPair: (pair) => callChecked(bff, OPS.sodPairRemove, pair, okViewSchema)
  };
}
var Ctx = createContext(null);
function PrivilegesAdminProvider({
  deps,
  children,
  privzOverride
}) {
  const [privz, setPrivz] = useState(privzOverride ?? null);
  const [privzError, setPrivzError] = useState(null);
  useEffect(() => {
    if (privzOverride !== void 0) return;
    let live = true;
    loadPrivileges().then((m) => {
      if (live) setPrivz(m);
    }).catch((e) => {
      if (live) setPrivzError(e instanceof Error ? e : new Error(String(e)));
    });
    return () => {
      live = false;
    };
  }, [privzOverride]);
  const client = useMemo(() => createPrivilegesClient(deps.client), [deps.client]);
  const snapshotSource = useMemo(
    () => deps.snapshotSource ?? (OPS.snapshotGet.served ? ({ personId, scope }) => client.snapshot(scope, personId) : void 0),
    [deps.snapshotSource, client]
  );
  const value = useMemo(() => {
    const explainAvailability = snapshotSource === void 0 ? unsupported(
      OPS.snapshotGet.blockedReason ?? `The server does not serve "${OPS.snapshotGet.op}", so provenance cannot be explained here.`,
      OPS.snapshotGet.blockedBy
    ) : privzError !== null ? unsupported(
      `The local resolver could not be loaded (${privzError.message}), so provenance cannot be explained here. Every server answer on this page is unaffected.`
    ) : privz === null ? unsupported("The local resolver is still loading.") : { state: "available", disabled: false };
    return {
      client,
      deps,
      snapshotSource,
      privz,
      privzError,
      explainAvailability,
      nowUnix: deps.nowUnix ?? (() => Math.floor(Date.now() / 1e3))
    };
  }, [client, deps, snapshotSource, privz, privzError]);
  return /* @__PURE__ */ jsx(Ctx.Provider, { value, children });
}
var MissingProviderError = class extends Error {
  constructor() {
    super(
      "@norkron/web-privileges-admin: a component was rendered outside <PrivilegesAdminProvider>. Build the module with createPrivilegesAdminModule(deps) and register it with buildConsole, or wrap the subtree yourself."
    );
    this.name = "MissingProviderError";
  }
};
function usePrivilegesAdmin() {
  const ctx = useContext(Ctx);
  if (ctx === null) throw new MissingProviderError();
  return ctx;
}
function GuardedButton({
  availability,
  onClick,
  children,
  testId,
  tone = "default"
}) {
  const reasonId = useId();
  const inert = availability.state === "available" && onClick === void 0;
  const disabled = availability.disabled || inert;
  const reason = inert ? "This control has no action wired to it, so it is held disabled rather than rendered as a lie." : availability.reason;
  return /* @__PURE__ */ jsxs("span", { className: "privz-admin__guarded", children: [
    /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: `privz-admin__button privz-admin__button--${tone}`,
        "data-testid": testId,
        "data-wired": disabled ? void 0 : "true",
        disabled,
        "aria-describedby": disabled && reason ? reasonId : void 0,
        "data-unavailable-reason": disabled ? reason ?? void 0 : void 0,
        onClick: disabled ? void 0 : onClick,
        children
      }
    ),
    disabled && reason ? /* @__PURE__ */ jsxs("span", { className: "privz-admin__reason", id: reasonId, "data-testid": `${testId}--reason`, children: [
      reason,
      availability.blockedBy ? /* @__PURE__ */ jsxs("span", { className: "privz-admin__reason-ref", children: [
        " (",
        availability.blockedBy,
        ")"
      ] }) : null
    ] }) : null
  ] });
}
function UnavailableNotice({
  availability,
  what,
  testId
}) {
  if (availability.state === "available") return null;
  return /* @__PURE__ */ jsxs("p", { className: "privz-admin__notice", "data-testid": testId, role: "note", children: [
    /* @__PURE__ */ jsxs("strong", { className: "privz-admin__notice-lead", children: [
      what,
      " is read-only here."
    ] }),
    " ",
    availability.reason,
    availability.blockedBy ? /* @__PURE__ */ jsxs("span", { className: "privz-admin__reason-ref", children: [
      " (",
      availability.blockedBy,
      ")"
    ] }) : null
  ] });
}
function AccessReviewCampaign({
  campaigns,
  createAvailability = AVAILABLE,
  decideAvailability = AVAILABLE,
  closeAvailability = AVAILABLE,
  onCreate,
  onDecide,
  onClose
}) {
  const [name, setName] = useState("");
  return /* @__PURE__ */ jsxs(
    DataPanel,
    {
      title: "Access reviews",
      meta: `${formatNumber(campaigns.length)} campaigns`,
      testId: "privz-admin-access-reviews",
      children: [
        /* @__PURE__ */ jsxs(
          "form",
          {
            className: "privz-admin__form",
            "data-testid": "privz-admin-access-reviews__new",
            onSubmit: (e) => e.preventDefault(),
            children: [
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Campaign name" }),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "text",
                    value: name,
                    "data-testid": "privz-admin-access-reviews__name",
                    onChange: (e) => setName(e.target.value)
                  }
                )
              ] }),
              /* @__PURE__ */ jsx(
                GuardedButton,
                {
                  availability: createAvailability,
                  testId: "privz-admin-access-reviews__create",
                  onClick: onCreate && name.trim().length > 0 ? () => {
                    onCreate({ name: name.trim() });
                    setName("");
                  } : void 0,
                  children: "New campaign"
                }
              )
            ]
          }
        ),
        campaigns.length === 0 ? /* @__PURE__ */ jsx(
          EmptyState,
          {
            title: "No campaigns",
            description: "No recertification campaign is open. Opening one names the access that has to be attested and by whom."
          }
        ) : campaigns.map((c) => /* @__PURE__ */ jsxs(
          "section",
          {
            "aria-label": `Campaign ${c.name}`,
            "data-testid": `privz-admin-campaign--${c.id}`,
            children: [
              /* @__PURE__ */ jsxs("h4", { className: "privz-admin__subhead", children: [
                c.name,
                /* @__PURE__ */ jsx(BadgePill, { tone: c.status === "closed" ? "neutral" : "warning", size: "sm", children: c.status }),
                c.status === "closed" ? null : /* @__PURE__ */ jsx(
                  GuardedButton,
                  {
                    availability: closeAvailability,
                    testId: `privz-admin-campaign__close--${c.id}`,
                    onClick: onClose ? () => onClose(c.id) : void 0,
                    children: "Close campaign"
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs("p", { className: "privz-admin__muted", children: [
                c.scope,
                " \xB7 ",
                c.period,
                " \xB7 due ",
                c.due_at,
                " \xB7",
                " ",
                formatNumber(c.attested_count),
                " of ",
                formatNumber(c.total_items),
                " attested",
                c.total_items > 0 ? ` (${formatPercent(c.attested_count / c.total_items)})` : "",
                " ",
                "\xB7 ",
                formatNumber(c.revoked_count),
                " revoked \xB7 reviewers ",
                c.reviewers.join(", ")
              ] }),
              /* @__PURE__ */ jsx("ul", { className: "privz-admin__list", "data-testid": `privz-admin-campaign__items--${c.id}`, children: c.items.map((it, i) => /* @__PURE__ */ jsxs(
                "li",
                {
                  className: "privz-admin__list-row",
                  "data-testid": `privz-admin-campaign-item--${c.id}--${i}`,
                  children: [
                    /* @__PURE__ */ jsx("span", { children: it.staff_label }),
                    /* @__PURE__ */ jsx("code", { className: "privz-admin__cap-key", children: it.capability }),
                    /* @__PURE__ */ jsx(
                      BadgePill,
                      {
                        tone: it.decision === "certified" ? "success" : it.decision === "revoked" ? "danger" : "warning",
                        size: "sm",
                        children: it.decision
                      }
                    ),
                    /* @__PURE__ */ jsx("span", { className: "privz-admin__muted", children: it.reviewer_label ?? "unreviewed" }),
                    /* @__PURE__ */ jsx(
                      GuardedButton,
                      {
                        availability: decideAvailability,
                        testId: `privz-admin-campaign-item__certify--${c.id}--${i}`,
                        onClick: onDecide ? () => onDecide(c.id, {
                          staff_label: it.staff_label,
                          capability: it.capability,
                          decision: "certified"
                        }) : void 0,
                        children: "Certify"
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      GuardedButton,
                      {
                        availability: decideAvailability,
                        tone: "danger",
                        testId: `privz-admin-campaign-item__revoke--${c.id}--${i}`,
                        onClick: onDecide ? () => onDecide(c.id, {
                          staff_label: it.staff_label,
                          capability: it.capability,
                          decision: "revoked"
                        }) : void 0,
                        children: "Revoke"
                      }
                    )
                  ]
                },
                `${it.staff_label}:${it.capability}:${i}`
              )) })
            ]
          },
          c.id
        ))
      ]
    }
  );
}
var NO_BUNDLE_UPDATE = unsupported(
  "The server has no route that updates a bundle in place, so a bundle key, rank and segment are permanent once defined. Only the capability set can be changed."
);
function BundleEditor({
  bundles,
  vocabulary,
  capabilitiesOf,
  selectedBundleId,
  defineAvailability = AVAILABLE,
  setCapAvailability = AVAILABLE,
  onSelect,
  onDefine,
  onSetCapability,
  onRemoveCapability
}) {
  const [key, setKey] = useState("");
  const [scopeKind, setScopeKind] = useState("workspace");
  const [displayName, setDisplayName] = useState("");
  const selected = bundles.find((b) => b.id === selectedBundleId);
  const held = new Set(selected ? capabilitiesOf[selected.id] ?? [] : []);
  return /* @__PURE__ */ jsxs(DataPanel, { title: "Bundles", testId: "privz-admin-bundle-editor", children: [
    /* @__PURE__ */ jsx(
      UnavailableNotice,
      {
        availability: NO_BUNDLE_UPDATE,
        what: "Bundle identity (key, rank, segment)",
        testId: "privz-admin-bundle-editor__no-update"
      }
    ),
    bundles.length === 0 ? /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No bundles",
        description: "A bundle is a named set of capabilities that can be granted as one unit."
      }
    ) : /* @__PURE__ */ jsx("ul", { className: "privz-admin__list", "data-testid": "privz-admin-bundle-editor__list", children: bundles.map((b) => /* @__PURE__ */ jsxs(
      "li",
      {
        className: "privz-admin__list-row",
        "data-testid": `privz-admin-bundle--${b.id}`,
        "data-selected": b.id === selectedBundleId ? "true" : "false",
        children: [
          /* @__PURE__ */ jsx(
            GuardedButton,
            {
              availability: AVAILABLE,
              testId: `privz-admin-bundle__select--${b.id}`,
              onClick: onSelect ? () => onSelect(b.id) : void 0,
              children: b.key
            }
          ),
          /* @__PURE__ */ jsx(BadgePill, { tone: "neutral", size: "sm", children: b.scope_kind }),
          b.segment ? /* @__PURE__ */ jsx(BadgePill, { tone: "info", size: "sm", children: b.segment }) : null
        ]
      },
      b.id
    )) }),
    selected ? /* @__PURE__ */ jsxs("section", { "aria-label": `Capabilities of ${selected.key}`, children: [
      /* @__PURE__ */ jsxs("h4", { className: "privz-admin__subhead", children: [
        "Capabilities of ",
        selected.key
      ] }),
      /* @__PURE__ */ jsx("ul", { className: "privz-admin__caps", "data-testid": "privz-admin-bundle-editor__caps", children: vocabulary.map((v) => {
        const on = held.has(v.key);
        return /* @__PURE__ */ jsxs("li", { "data-testid": `privz-admin-bundle-cap--${v.key}`, "data-granted": on ? "true" : "false", children: [
          /* @__PURE__ */ jsx("code", { className: "privz-admin__cap-key", children: v.key }),
          /* @__PURE__ */ jsx("span", { children: v.label }),
          /* @__PURE__ */ jsx(
            GuardedButton,
            {
              availability: setCapAvailability,
              tone: on ? "danger" : "default",
              testId: `privz-admin-bundle-cap__toggle--${v.key}`,
              onClick: on ? onRemoveCapability ? () => onRemoveCapability(selected.id, v.key) : void 0 : onSetCapability ? () => onSetCapability(selected.id, v.key, "allow") : void 0,
              children: on ? "Remove" : "Add"
            }
          )
        ] }, v.key);
      }) })
    ] }) : null,
    /* @__PURE__ */ jsxs("form", { className: "privz-admin__form", onSubmit: (e) => e.preventDefault(), children: [
      /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
        /* @__PURE__ */ jsx("span", { children: "New bundle key" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            value: key,
            "data-testid": "privz-admin-bundle-editor__key",
            onChange: (e) => setKey(e.target.value)
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
        /* @__PURE__ */ jsx("span", { children: "Scope kind" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            value: scopeKind,
            "data-testid": "privz-admin-bundle-editor__scope-kind",
            onChange: (e) => setScopeKind(e.target.value)
          }
        )
      ] }),
      /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
        /* @__PURE__ */ jsx("span", { children: "Display name" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            value: displayName,
            "data-testid": "privz-admin-bundle-editor__display-name",
            onChange: (e) => setDisplayName(e.target.value)
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        GuardedButton,
        {
          availability: defineAvailability,
          testId: "privz-admin-bundle-editor__define",
          onClick: onDefine && key.trim().length > 0 ? () => {
            onDefine({
              key: key.trim(),
              scope_kind: scopeKind.trim(),
              display_name: displayName.trim()
            });
            setKey("");
            setDisplayName("");
          } : void 0,
          children: "Define bundle"
        }
      )
    ] })
  ] });
}

// src/grid-model.ts
function bundleLines(bundles) {
  return bundles.map((bundle) => ({ kind: "bundle", id: bundle.id, bundle }));
}
function capabilityBlocks(grouped) {
  return grouped.map((g) => ({
    group: g.group,
    groupLabel: g.groupLabel,
    lines: g.entries.map((capability) => ({
      kind: "capability",
      id: capability.key,
      capability
    }))
  }));
}
function buildGridAxes(bundles, grouped, orientation) {
  const bundleBlock = { group: null, groupLabel: null, lines: bundleLines(bundles) };
  const capBlocks = capabilityBlocks(grouped);
  const rows = orientation === "bundles-as-rows" ? [bundleBlock] : capBlocks;
  const columns = orientation === "bundles-as-rows" ? capBlocks : [bundleBlock];
  const columnLines = columns.flatMap((b) => [...b.lines]);
  return {
    rows,
    columns,
    rowKind: orientation === "bundles-as-rows" ? "bundle" : "capability",
    columnKind: orientation === "bundles-as-rows" ? "capability" : "bundle",
    groupedAxis: orientation === "bundles-as-rows" ? "columns" : "rows",
    columnCount: columnLines.length,
    columnLines
  };
}
function cellCoords(a, b) {
  if (a.kind === "bundle" && b.kind === "capability") {
    return { bundleId: a.id, capabilityKey: b.id };
  }
  if (a.kind === "capability" && b.kind === "bundle") {
    return { bundleId: b.id, capabilityKey: a.id };
  }
  throw new Error(
    `@norkron/web-privileges-admin: a grid cell is the intersection of one bundle and one capability, but both axes handed a "${a.kind}". This is an axis-model bug, not bad data \u2014 buildGridAxes always puts bundles on one axis and capabilities on the other.`
  );
}
function cellStateKey(bundleId, capabilityKey) {
  return `${bundleId}${capabilityKey}`;
}
function bundleStateKeyPrefix(bundleId) {
  return `${bundleId}`;
}
function splitCellStateKey(key) {
  const at = key.indexOf("");
  if (at === -1) return { bundleId: key, capabilityKey: "" };
  return { bundleId: key.slice(0, at), capabilityKey: key.slice(at + 1) };
}
function cellTestId(bundleId, capabilityKey) {
  return `privz-admin-grid__cell--${bundleId}--${capabilityKey}`;
}
function heldIndex(matrix) {
  const held = /* @__PURE__ */ new Map();
  for (const b of matrix.bundles) held.set(b.id, new Set(b.capability_atoms));
  for (const cell of matrix.matrix) {
    if (!cell.granted) continue;
    const s = held.get(cell.bundle_id);
    if (s) s.add(cell.capability_key);
  }
  return held;
}
function cellIndex(matrix) {
  const out = /* @__PURE__ */ new Map();
  for (const cell of matrix.matrix) {
    out.set(cellStateKey(cell.bundle_id, cell.capability_key), cell);
  }
  return out;
}
function matrixSignature(matrix) {
  const parts = [];
  for (const b of matrix.bundles) {
    parts.push(
      `${b.id}:${b.editable ? "1" : "0"}:${b.member_count}:${[...b.capability_atoms].sort().join("|")}`
    );
  }
  for (const c of matrix.matrix) {
    const provenance = c.is_default === void 0 ? "" : c.is_default ? "d" : "c";
    parts.push(`${c.bundle_id}${c.capability_key}=${c.granted ? "1" : "0"}${provenance}`);
  }
  return parts.join(";");
}
var NO_CELLS = Object.freeze({});
function withoutSeq(starts, seq) {
  if (!(seq in starts)) return starts;
  const next = {};
  for (const [k, v] of Object.entries(starts)) {
    if (Number(k) !== seq) next[Number(k)] = v;
  }
  return next;
}
function optimisticReducer(state, action) {
  switch (action.type) {
    case "begin": {
      const prev = state[action.key];
      return {
        ...state,
        [action.key]: {
          value: action.to,
          pending: (prev?.pending ?? 0) + 1,
          latestSeq: action.seq,
          starts: { ...prev?.starts ?? {}, [action.seq]: action.from },
          // A retry clears the previous failure: the user has acted again, and a
          // stale error beside a live attempt reads as "this is still broken".
          error: null
        }
      };
    }
    case "settle": {
      const prev = state[action.key];
      if (prev === void 0) return state;
      return {
        ...state,
        [action.key]: {
          ...prev,
          pending: Math.max(0, prev.pending - 1),
          starts: withoutSeq(prev.starts, action.seq)
        }
      };
    }
    case "reject": {
      const prev = state[action.key];
      if (prev === void 0) return state;
      const revertTo = prev.starts[action.seq];
      const superseded = action.seq !== prev.latestSeq;
      return {
        ...state,
        [action.key]: {
          value: superseded || revertTo === void 0 ? prev.value : revertTo,
          pending: Math.max(0, prev.pending - 1),
          latestSeq: prev.latestSeq,
          starts: withoutSeq(prev.starts, action.seq),
          error: action.error
        }
      };
    }
    case "clear": {
      const keys = Object.keys(state).filter((k) => k.startsWith(action.keyPrefix));
      if (keys.length === 0) return state;
      const next = {};
      for (const [k, v] of Object.entries(state)) {
        if (!k.startsWith(action.keyPrefix)) next[k] = v;
      }
      return next;
    }
    case "server-refresh": {
      const drop = Object.entries(state).filter(([, v]) => v.pending === 0 && v.error === null);
      if (drop.length === 0) return state;
      const next = {};
      for (const [k, v] of Object.entries(state)) {
        if (v.pending > 0 || v.error !== null) next[k] = v;
      }
      return next;
    }
  }
}
function cellValue(state, key, base) {
  return state[key]?.value ?? base;
}
function cellPending(state, key) {
  return (state[key]?.pending ?? 0) > 0;
}
function cellError(state, key) {
  return state[key]?.error ?? null;
}
function pendingKeys(state) {
  return Object.keys(state).filter((k) => (state[k]?.pending ?? 0) > 0).sort();
}
function failedKeys(state) {
  return Object.keys(state).filter((k) => state[k]?.error !== null && state[k]?.error !== void 0).sort();
}
function messageOf(reason) {
  if (reason instanceof Error && reason.message.length > 0) return reason.message;
  const text = typeof reason === "string" ? reason : String(reason);
  return text.length > 0 && text !== "undefined" && text !== "null" ? text : "The server refused this change without giving a reason.";
}
function useOptimisticCells() {
  const [cells, dispatch] = useReducer(optimisticReducer, NO_CELLS);
  const seqRef = useRef(0);
  const commit = useCallback(
    (key, from, to, write) => {
      seqRef.current += 1;
      const seq = seqRef.current;
      dispatch({ type: "begin", key, seq, from, to });
      let result;
      try {
        result = write();
      } catch (e) {
        dispatch({ type: "reject", key, seq, error: messageOf(e) });
        return;
      }
      if (result !== void 0 && typeof result.then === "function") {
        void result.then(
          () => dispatch({ type: "settle", key, seq }),
          (e) => dispatch({ type: "reject", key, seq, error: messageOf(e) })
        );
        return;
      }
      dispatch({ type: "settle", key, seq });
    },
    []
  );
  const clear = useCallback((keyPrefix) => {
    dispatch({ type: "clear", keyPrefix });
  }, []);
  const refresh = useCallback(() => {
    dispatch({ type: "server-refresh" });
  }, []);
  return useMemo(
    () => ({
      cells,
      value: (key, base) => cellValue(cells, key, base),
      pending: (key) => cellPending(cells, key),
      error: (key) => cellError(cells, key),
      commit,
      clear,
      refresh
    }),
    [cells, commit, clear, refresh]
  );
}

// src/vocabulary.ts
function deriveGroup(key) {
  const parts = key.split(".").filter((p) => p.length > 0);
  if (parts.length === 0) return key;
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return parts[0];
  return parts[1];
}
function assertGenericVocabulary(overrides) {
  const groupIds = /* @__PURE__ */ new Set();
  for (const d of Object.values(overrides.capabilities ?? {})) {
    if (d.group) groupIds.add(d.group);
  }
  for (const g of Object.keys(overrides.groups ?? {})) groupIds.add(g);
  for (const g of overrides.groupOrder ?? []) groupIds.add(g);
  assertGenericIdentifiers({
    "vocabulary override: capability keys": Object.keys(overrides.capabilities ?? {}),
    "vocabulary override: group ids": [...groupIds]
  });
}
function normalise(raw, overrides) {
  const o = overrides.capabilities?.[raw.key];
  const serverLabel = raw.label ?? raw.display_name ?? "";
  const label = o?.label ?? (serverLabel.length > 0 ? serverLabel : raw.key);
  const serverGroup = raw.group ?? raw.namespace ?? "";
  const group = o?.group ?? (serverGroup.length > 0 ? serverGroup : deriveGroup(raw.key));
  const groupLabel = o?.groupLabel ?? overrides.groups?.[group] ?? raw.group_label ?? group;
  return {
    key: raw.key,
    label,
    description: o?.description ?? raw.description ?? "",
    group,
    groupLabel,
    sensitive: o?.sensitive ?? raw.sensitive ?? false,
    deprecated: raw.deprecated ?? false,
    order: o?.order ?? Number.MAX_SAFE_INTEGER,
    origin: {
      label: o?.label !== void 0 ? "override" : serverLabel.length > 0 ? "server" : "derived",
      group: o?.group !== void 0 ? "override" : serverGroup.length > 0 ? "server" : "derived"
    }
  };
}
function sortEntries(entries, overrides) {
  const groupRank = /* @__PURE__ */ new Map();
  (overrides.groupOrder ?? []).forEach((g, i) => groupRank.set(g, i));
  const rank = (g) => groupRank.get(g) ?? Number.MAX_SAFE_INTEGER;
  return [...entries].sort((a, b) => {
    const ga = rank(a.group);
    const gb = rank(b.group);
    if (ga !== gb) return ga - gb;
    if (a.group !== b.group) return a.group.localeCompare(b.group);
    if (a.order !== b.order) return a.order - b.order;
    return a.key.localeCompare(b.key);
  });
}
function buildVocabulary(rows, overrides = {}) {
  assertGenericVocabulary(overrides);
  return sortEntries(
    rows.map((r) => normalise(r, overrides)),
    overrides
  );
}
function buildVocabularyFromAtoms(atoms, overrides = {}) {
  assertGenericVocabulary(overrides);
  return sortEntries(
    atoms.map((a) => normalise(a, overrides)),
    overrides
  );
}
function groupVocabulary(entries) {
  const out = [];
  for (const e of entries) {
    const last = out[out.length - 1];
    if (last && last.group === e.group) last.entries.push(e);
    else out.push({ group: e.group, groupLabel: e.groupLabel, entries: [e] });
  }
  return out;
}
var SYSTEM_MANAGED = "This bundle is marked system-managed by the server, so its capabilities cannot be edited from any console.";
var NO_SAVE_HANDLER = "This grid was rendered without a save handler, so its cells are held read-only rather than accepting edits that go nowhere.";
var NO_TOGGLE_HANDLER = "This grid was asked to commit one cell at a time but given no onToggleCell handler, so its cells are held read-only rather than accepting edits that go nowhere.";
function linesOf(rowLine, colLine) {
  if (rowLine.kind === "bundle" && colLine.kind === "capability") {
    return { bundle: rowLine, capability: colLine };
  }
  if (rowLine.kind === "capability" && colLine.kind === "bundle") {
    return { bundle: colLine, capability: rowLine };
  }
  throw new Error(
    `@norkron/web-privileges-admin: a grid cell is one bundle \xD7 one capability, but both axes handed a "${rowLine.kind}".`
  );
}
function CapabilityGrid({
  matrix,
  vocabulary,
  editAvailability = AVAILABLE,
  onSaveBundle,
  orientation = "capabilities-as-rows",
  commit = "draft-full-set",
  onToggleCell,
  onResetBundle,
  resetAvailability,
  matrixVersion
}) {
  const [draft, setDraft] = useState(null);
  const optimistic = useOptimisticCells();
  const perCell = commit === "per-cell";
  const granted = useMemo(() => heldIndex(matrix), [matrix]);
  const cells = useMemo(() => cellIndex(matrix), [matrix]);
  const grouped = useMemo(
    () => groupVocabulary(buildVocabularyFromAtoms(matrix.capabilities, vocabulary)),
    [matrix.capabilities, vocabulary]
  );
  const axes = useMemo(
    () => buildGridAxes(matrix.bundles, grouped, orientation),
    [matrix.bundles, grouped, orientation]
  );
  const signature = useMemo(() => matrixSignature(matrix), [matrix]);
  const reconcileKey = `${matrixVersion ?? ""}|${signature}`;
  const lastReconciled = useRef(reconcileKey);
  if (lastReconciled.current !== reconcileKey) {
    lastReconciled.current = reconcileKey;
    optimistic.refresh();
  }
  const base = (bundleId, cap) => granted.get(bundleId)?.has(cap) ?? false;
  const held = (bundleId, cap) => perCell ? optimistic.value(cellStateKey(bundleId, cap), base(bundleId, cap)) : (draft?.[bundleId] ?? granted.get(bundleId))?.has(cap) ?? false;
  const toggleDraft = (bundleId, cap) => {
    setDraft((prev) => {
      const from = prev?.[bundleId] ?? new Set(granted.get(bundleId) ?? []);
      const next = new Set(from);
      if (next.has(cap)) next.delete(cap);
      else next.add(cap);
      return { ...prev ?? {}, [bundleId]: next };
    });
  };
  const toggleCell = (bundleId, cap) => {
    if (onToggleCell === void 0) return;
    const key = cellStateKey(bundleId, cap);
    const from = optimistic.value(key, base(bundleId, cap));
    optimistic.commit(key, from, !from, () => onToggleCell(bundleId, cap, !from));
  };
  const resetBundle = (bundleId) => {
    if (onResetBundle === void 0) return;
    optimistic.clear(bundleStateKeyPrefix(bundleId));
    void onResetBundle(bundleId);
  };
  if (matrix.bundles.length === 0) {
    return /* @__PURE__ */ jsx(DataPanel, { title: "Capability grid", testId: "privz-admin-capability-grid", children: /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No bundles defined",
        description: "The server returned no role bundles for this scope, so there is nothing to grid."
      }
    ) });
  }
  const headingOf = (line) => {
    if (line.kind === "capability") {
      return /* @__PURE__ */ jsxs(Fragment, { children: [
        line.capability.label,
        /* @__PURE__ */ jsx("code", { className: "privz-admin__cap-key", children: line.capability.key })
      ] });
    }
    const b = line.bundle;
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      b.label || b.key,
      /* @__PURE__ */ jsxs("span", { className: "privz-admin__grid-sub", children: [
        formatNumber(b.member_count),
        " members",
        b.editable ? "" : " \xB7 system"
      ] }),
      onResetBundle === void 0 ? null : /* @__PURE__ */ jsx(
        GuardedButton,
        {
          availability: b.editable ? resetAvailability ?? editAvailability : unavailable(SYSTEM_MANAGED),
          testId: `privz-admin-grid__reset--${b.id}`,
          onClick: () => resetBundle(b.id),
          children: "Reset to defaults"
        }
      )
    ] });
  };
  const headingProps = (line, axis) => {
    if (line.kind === "bundle") {
      return { "data-testid": `privz-admin-grid__bundle--${line.id}` };
    }
    return {
      className: line.capability.sensitive ? "privz-admin__cap--sensitive" : void 0,
      // ONE handle per line. A capability in the ROW position is already named by
      // its `<tr>`'s `…__row--<key>`, so a second testid there would change a render
      // existing consumers' selectors are written against for no new addressability.
      // In the COLUMN position the heading is the line's only handle, so it gets one.
      "data-testid": axis === "col" ? `privz-admin-grid__capability--${line.id}` : void 0
    };
  };
  const renderCell = (rowLine, colLine) => {
    const { bundleId, capabilityKey } = cellCoords(rowLine, colLine);
    const { bundle: bundleLine, capability: capLine } = linesOf(rowLine, colLine);
    const b = bundleLine.bundle;
    const cap = capLine.capability;
    const bundleLabel = b.label || b.key;
    const stateKey = cellStateKey(bundleId, capabilityKey);
    const on = held(bundleId, capabilityKey);
    const pending = perCell && optimistic.pending(stateKey);
    const failure = perCell ? optimistic.error(stateKey) : null;
    const provenance = cells.get(stateKey)?.is_default;
    const customised = provenance === false;
    const writerMissing = perCell ? onToggleCell === void 0 : onSaveBundle === void 0;
    const cellAvail = b.editable ? editAvailability : unavailable(SYSTEM_MANAGED);
    const disabled = cellAvail.disabled || writerMissing;
    return /* @__PURE__ */ jsxs("td", { children: [
      /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          role: "switch",
          "aria-checked": on,
          "aria-label": `${cap.label} in ${bundleLabel}${customised ? " \u2014 customised, not the default" : ""}`,
          "aria-busy": pending || void 0,
          className: "privz-admin__cell",
          "data-testid": cellTestId(bundleId, capabilityKey),
          "data-granted": on ? "true" : "false",
          "data-custom": provenance === void 0 ? void 0 : customised ? "true" : "false",
          "data-pending": pending ? "true" : void 0,
          "data-commit-error": failure ?? void 0,
          "data-wired": disabled ? void 0 : "true",
          disabled,
          "data-unavailable-reason": cellAvail.disabled ? cellAvail.reason : writerMissing ? perCell ? NO_TOGGLE_HANDLER : NO_SAVE_HANDLER : void 0,
          onClick: disabled ? void 0 : perCell ? () => toggleCell(bundleId, capabilityKey) : () => toggleDraft(bundleId, capabilityKey),
          children: on ? "\u2713" : "\xB7"
        }
      ),
      customised ? (
        // Decoration for the eye, a handle for a test. The same fact reaches
        // assistive tech through the switch's own accessible name above, so the
        // marker is hidden from it rather than announced twice.
        /* @__PURE__ */ jsx(
          "span",
          {
            className: "privz-admin__grid-custom",
            "data-testid": `privz-admin-grid__custom--${bundleId}--${capabilityKey}`,
            title: "Customised \u2014 an explicit override rather than the server's default.",
            "aria-hidden": "true",
            children: "\u25C6"
          }
        )
      ) : null
    ] }, colLine.id);
  };
  const inFlight = perCell ? Object.keys(optimistic.cells).filter((k) => optimistic.pending(k)) : [];
  const failures = perCell ? Object.keys(optimistic.cells).filter((k) => optimistic.error(k) !== null).sort() : [];
  return /* @__PURE__ */ jsxs(
    DataPanel,
    {
      title: "Capability grid",
      meta: `${formatNumber(matrix.bundles.length)} bundles \xB7 ${formatNumber(matrix.capabilities.length)} capabilities`,
      testId: "privz-admin-capability-grid",
      children: [
        perCell || onToggleCell === void 0 ? null : /* @__PURE__ */ jsxs(
          "p",
          {
            className: "privz-admin__notice",
            role: "note",
            "data-testid": "privz-admin-capability-grid__ignored-toggle",
            children: [
              /* @__PURE__ */ jsx("strong", { className: "privz-admin__notice-lead", children: "onToggleCell is not being used." }),
              ' This grid was given a per-cell handler while its commit mode is the default draft-full-set, so toggles edit a local draft and that handler is never called. Pass commit="per-cell" to use it, or drop it \u2014 a handler that looks wired and is not is exactly the defect this notice exists to prevent.'
            ]
          }
        ),
        /* @__PURE__ */ jsx("div", { className: "privz-admin__grid-scroll", children: /* @__PURE__ */ jsxs("table", { className: "privz-admin__grid", "data-testid": "privz-admin-capability-grid__table", children: [
          /* @__PURE__ */ jsx("caption", { className: "privz-admin__caption", children: perCell ? "Which capabilities each role bundle carries. Toggling a cell writes that one cell immediately: the change is painted at once, held busy until the server answers, and put back if it is refused." : "Which capabilities each role bundle carries. Toggling a cell edits a local draft; saving sends the bundle's complete capability set, because the server replaces rather than merges." }),
          /* @__PURE__ */ jsxs("thead", { children: [
            axes.groupedAxis === "columns" ? /* @__PURE__ */ jsxs("tr", { className: "privz-admin__grid-colgrouprow", children: [
              /* @__PURE__ */ jsx("td", { className: "privz-admin__grid-corner" }),
              axes.columns.map((block) => /* @__PURE__ */ jsx(
                "th",
                {
                  scope: "colgroup",
                  colSpan: block.lines.length,
                  "data-testid": block.group === null ? void 0 : `privz-admin-grid__group--${block.group}`,
                  children: block.groupLabel
                },
                block.group ?? "\xB7"
              ))
            ] }) : null,
            /* @__PURE__ */ jsxs("tr", { children: [
              /* @__PURE__ */ jsx("th", { scope: "col", children: axes.rowKind === "bundle" ? "Role bundle" : "Capability" }),
              axes.columnLines.map((line) => /* @__PURE__ */ jsx("th", { scope: "col", ...headingProps(line, "col"), children: headingOf(line) }, line.id))
            ] })
          ] }),
          axes.rows.map((block) => /* @__PURE__ */ jsxs(
            "tbody",
            {
              "data-testid": block.group === null ? void 0 : `privz-admin-grid__group--${block.group}`,
              children: [
                block.group === null ? null : /* @__PURE__ */ jsx("tr", { className: "privz-admin__grid-grouprow", children: /* @__PURE__ */ jsx("th", { scope: "colgroup", colSpan: axes.columnCount + 1, children: block.groupLabel }) }),
                block.lines.map((rowLine) => /* @__PURE__ */ jsxs("tr", { "data-testid": `privz-admin-grid__row--${rowLine.id}`, children: [
                  /* @__PURE__ */ jsx("th", { scope: "row", ...headingProps(rowLine, "row"), children: headingOf(rowLine) }),
                  axes.columnLines.map((colLine) => renderCell(rowLine, colLine))
                ] }, rowLine.id))
              ]
            },
            block.group ?? "\xB7"
          ))
        ] }) }),
        perCell ? /* @__PURE__ */ jsxs(
          "div",
          {
            className: "privz-admin__grid-live",
            role: "status",
            "aria-live": "polite",
            "data-testid": "privz-admin-capability-grid__status",
            children: [
              inFlight.length > 0 ? `${formatNumber(inFlight.length)} change${inFlight.length === 1 ? "" : "s"} in flight.` : null,
              failures.length > 0 ? /* @__PURE__ */ jsx("ul", { className: "privz-admin__list", children: failures.map((k) => {
                const { bundleId, capabilityKey } = splitCellStateKey(k);
                return /* @__PURE__ */ jsxs(
                  "li",
                  {
                    className: "privz-admin__reason",
                    "data-testid": `privz-admin-grid__error--${bundleId}--${capabilityKey}`,
                    children: [
                      "The server refused this change and it has been put back: ",
                      optimistic.error(k)
                    ]
                  },
                  k
                );
              }) }) : null
            ]
          }
        ) : null,
        draft !== null && onSaveBundle !== void 0 ? /* @__PURE__ */ jsx("div", { className: "privz-admin__grid-actions", children: Object.entries(draft).map(([bundleId, caps]) => /* @__PURE__ */ jsxs(
          GuardedButton,
          {
            availability: editAvailability,
            testId: `privz-admin-grid__save--${bundleId}`,
            onClick: () => {
              onSaveBundle(bundleId, [...caps].sort());
              setDraft(null);
            },
            children: [
              "Save ",
              matrix.bundles.find((b) => b.id === bundleId)?.label ?? bundleId,
              " (",
              formatNumber(caps.size),
              " capabilities)"
            ]
          },
          bundleId
        )) }) : null
      ]
    }
  );
}
var ELEVATION_KINDS = ["jit", "pim"];
function Row({
  grant,
  approveAvailability,
  onApprove
}) {
  return /* @__PURE__ */ jsxs("li", { className: "privz-admin__list-row", "data-testid": `privz-admin-elevation--${grant.id}`, children: [
    /* @__PURE__ */ jsx("span", { children: grant.staff_label }),
    /* @__PURE__ */ jsx("code", { className: "privz-admin__cap-key", children: grant.capability }),
    /* @__PURE__ */ jsx(BadgePill, { tone: grant.status === "active" ? "success" : "warning", size: "sm", children: grant.status }),
    /* @__PURE__ */ jsx(BadgePill, { tone: "neutral", size: "sm", children: grant.kind }),
    /* @__PURE__ */ jsx("span", { className: "privz-admin__muted", children: grant.reason }),
    /* @__PURE__ */ jsx("time", { dateTime: grant.requested_at, children: grant.requested_at }),
    grant.expires_at ? /* @__PURE__ */ jsxs("time", { dateTime: grant.expires_at, children: [
      "expires ",
      grant.expires_at
    ] }) : null,
    grant.status === "pending" ? /* @__PURE__ */ jsx(
      GuardedButton,
      {
        availability: approveAvailability,
        testId: `privz-admin-elevation__approve--${grant.id}`,
        onClick: onApprove ? () => onApprove(grant.id) : void 0,
        children: "Approve"
      }
    ) : null
  ] });
}
function ElevationBoard({
  board,
  approveAvailability = AVAILABLE,
  onApprove,
  capabilityKeys = [],
  requestAvailability = AVAILABLE,
  onRequest
}) {
  const [capability, setCapability] = useState("");
  const [kind, setKind] = useState(ELEVATION_KINDS[0]);
  const [reason, setReason] = useState("");
  const ready = capability.length > 0 && reason.trim().length > 0;
  return /* @__PURE__ */ jsxs(
    DataPanel,
    {
      title: "Elevation board",
      meta: `${formatNumber(board.pending.length)} pending \xB7 ${formatNumber(board.active.length)} active`,
      testId: "privz-admin-elevation-board",
      children: [
        /* @__PURE__ */ jsxs(
          "form",
          {
            className: "privz-admin__form",
            "data-testid": "privz-admin-elevation-board__request-form",
            onSubmit: (e) => e.preventDefault(),
            children: [
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Capability" }),
                /* @__PURE__ */ jsxs(
                  "select",
                  {
                    value: capability,
                    "data-testid": "privz-admin-elevation-board__capability",
                    "data-wired": "true",
                    onChange: (e) => setCapability(e.target.value),
                    children: [
                      /* @__PURE__ */ jsx("option", { value: "", children: "Choose a capability\u2026" }),
                      capabilityKeys.map((k) => /* @__PURE__ */ jsx("option", { value: k, children: k }, k))
                    ]
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Kind" }),
                /* @__PURE__ */ jsx(
                  "select",
                  {
                    value: kind,
                    "data-testid": "privz-admin-elevation-board__kind",
                    "data-wired": "true",
                    onChange: (e) => setKind(e.target.value),
                    children: ELEVATION_KINDS.map((k) => /* @__PURE__ */ jsx("option", { value: k, children: k }, k))
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Reason" }),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "text",
                    value: reason,
                    "data-testid": "privz-admin-elevation-board__reason",
                    onChange: (e) => setReason(e.target.value)
                  }
                )
              ] }),
              /* @__PURE__ */ jsx(
                GuardedButton,
                {
                  availability: requestAvailability,
                  testId: "privz-admin-elevation-board__request",
                  onClick: onRequest && ready ? () => {
                    onRequest({ capability, kind, reason: reason.trim() });
                    setCapability("");
                    setReason("");
                  } : void 0,
                  children: "Request elevation"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs("p", { className: "privz-admin__muted", "data-testid": "privz-admin-elevation-board__request-note", children: [
          "An elevation you request is born ",
          /* @__PURE__ */ jsx("strong", { children: "pending" }),
          " and grants nothing until someone approves it. You can only request access for yourself."
        ] }),
        /* @__PURE__ */ jsx("p", { className: "privz-admin__muted", "data-testid": "privz-admin-elevation-board__scope-note", children: "This board shows only active and pending elevations. Expired and denied grants are dropped by the server before the response is built, so this is a live view and not an audit trail." }),
        /* @__PURE__ */ jsxs("section", { "aria-label": "Pending elevations", children: [
          /* @__PURE__ */ jsx("h4", { className: "privz-admin__subhead", children: "Pending" }),
          board.pending.length === 0 ? /* @__PURE__ */ jsx(EmptyState, { title: "Nothing pending", description: "No elevation is waiting for approval." }) : /* @__PURE__ */ jsx("ul", { className: "privz-admin__list", "data-testid": "privz-admin-elevation-board__pending", children: board.pending.map((g) => /* @__PURE__ */ jsx(
            Row,
            {
              grant: g,
              approveAvailability,
              onApprove
            },
            g.id
          )) })
        ] }),
        /* @__PURE__ */ jsxs("section", { "aria-label": "Active elevations", children: [
          /* @__PURE__ */ jsx("h4", { className: "privz-admin__subhead", children: "Active" }),
          board.active.length === 0 ? /* @__PURE__ */ jsx(EmptyState, { title: "Nothing active", description: "No elevated access is live right now." }) : /* @__PURE__ */ jsx("ul", { className: "privz-admin__list", "data-testid": "privz-admin-elevation-board__active", children: board.active.map((g) => /* @__PURE__ */ jsx(
            Row,
            {
              grant: g,
              approveAvailability,
              onApprove
            },
            g.id
          )) })
        ] })
      ]
    }
  );
}

// src/provenance.ts
var BUNDLE_PREFIX = "bundle:";
var DERIVED_PREFIX = "derived:";
function parseProvenance(raw, viaGroup) {
  const base = () => {
    if (raw === "direct") {
      return { kind: "direct", raw, label: "Granted directly" };
    }
    if (raw.startsWith(BUNDLE_PREFIX)) {
      const detail = raw.slice(BUNDLE_PREFIX.length);
      return { kind: "bundle", raw, detail, label: `From bundle "${detail}"` };
    }
    if (raw.startsWith(DERIVED_PREFIX)) {
      const detail = raw.slice(DERIVED_PREFIX.length);
      return { kind: "derived", raw, detail, label: `Derived from rule "${detail}"` };
    }
    return { kind: "custom", raw, label: `Recorded source "${raw}"` };
  };
  const p = base();
  if (viaGroup === void 0) return p;
  return {
    ...p,
    kind: "group",
    viaGroup,
    label: `${p.label}, via group "${viaGroup}"`
  };
}
function toResolvedCapabilities(entries, groupOf) {
  return entries.map((e) => ({
    capability: e.capability,
    provenance: parseProvenance(e.source, groupOf?.(e.source, e.capability))
  }));
}
var SHORT = {
  direct: "direct",
  bundle: "bundle",
  derived: "derived",
  group: "group",
  custom: "recorded"
};
function ProvenanceChip({ provenance, testId }) {
  const text = provenance.detail ? `${SHORT[provenance.kind]}: ${provenance.detail}` : SHORT[provenance.kind];
  return /* @__PURE__ */ jsx(
    RichTooltip,
    {
      content: provenance.label,
      trigger: /* @__PURE__ */ jsx(
        "span",
        {
          className: `privz-admin__chip privz-admin__chip--${provenance.kind}`,
          "data-testid": testId,
          "data-provenance-kind": provenance.kind,
          "data-provenance-raw": provenance.raw,
          title: provenance.label,
          children: text
        }
      )
    }
  );
}
var SUBJECT_KINDS = ["user", "account", "group"];
function GrantTable({
  scope,
  grants,
  capabilityKeys,
  createAvailability = AVAILABLE,
  revokeAvailability = AVAILABLE,
  onCreate,
  onRevoke,
  onDraftChange
}) {
  const [subjectKind, setSubjectKind] = useState("user");
  const [subjectId, setSubjectId] = useState("");
  const [capability, setCapability] = useState("");
  const [effect2, setEffect] = useState("allow");
  const ready = subjectId.trim().length > 0 && capability.length > 0;
  return /* @__PURE__ */ jsxs(
    DataPanel,
    {
      title: "Grants",
      meta: `${scope.scope_kind}:${scope.scope_id}`,
      testId: "privz-admin-grant-table",
      children: [
        grants.length === 0 ? /* @__PURE__ */ jsx(
          EmptyState,
          {
            title: "No grants on this scope",
            description: "Nothing has been granted here directly. Capabilities may still reach a principal through a bundle, a group, or an account-level rule."
          }
        ) : /* @__PURE__ */ jsxs("table", { className: "privz-admin__table", "data-testid": "privz-admin-grant-table__table", children: [
          /* @__PURE__ */ jsx("caption", { className: "privz-admin__caption", children: "Grants recorded on this exact scope. A grant on a different scope will not appear here even if it reaches the same person." }),
          /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
            /* @__PURE__ */ jsx("th", { scope: "col", children: "Subject" }),
            /* @__PURE__ */ jsx("th", { scope: "col", children: "Capability or bundle" }),
            /* @__PURE__ */ jsx("th", { scope: "col", children: "Effect" }),
            /* @__PURE__ */ jsx("th", { scope: "col", children: "Recorded source" }),
            /* @__PURE__ */ jsx("th", { scope: "col", children: "Conditions" }),
            /* @__PURE__ */ jsx("th", { scope: "col", children: "Action" })
          ] }) }),
          /* @__PURE__ */ jsx("tbody", { children: grants.map((g) => /* @__PURE__ */ jsxs("tr", { "data-testid": `privz-admin-grant--${g.id}`, children: [
            /* @__PURE__ */ jsx("td", { children: /* @__PURE__ */ jsxs("code", { className: "privz-admin__key", children: [
              g.subject_kind,
              ":",
              g.subject_id
            ] }) }),
            /* @__PURE__ */ jsx("td", { children: /* @__PURE__ */ jsx("code", { className: "privz-admin__cap-key", children: g.capability ?? (g.bundle_key ? `bundle ${g.bundle_key}` : "\u2014") }) }),
            /* @__PURE__ */ jsx("td", { children: /* @__PURE__ */ jsx(BadgePill, { tone: g.effect === "deny" ? "danger" : "success", size: "sm", children: g.effect }) }),
            /* @__PURE__ */ jsx("td", { children: /* @__PURE__ */ jsx(
              ProvenanceChip,
              {
                provenance: parseProvenance(g.source),
                testId: `privz-admin-grant__prov--${g.id}`
              }
            ) }),
            /* @__PURE__ */ jsx("td", { children: /* @__PURE__ */ jsx("code", { className: "privz-admin__conditions", children: g.conditions_json }) }),
            /* @__PURE__ */ jsx("td", { children: /* @__PURE__ */ jsx(
              GuardedButton,
              {
                availability: revokeAvailability,
                tone: "danger",
                testId: `privz-admin-grant__revoke--${g.id}`,
                onClick: onRevoke ? () => onRevoke(g.id) : void 0,
                children: "Revoke"
              }
            ) })
          ] }, g.id)) })
        ] }),
        /* @__PURE__ */ jsxs(
          "form",
          {
            className: "privz-admin__form",
            "data-testid": "privz-admin-grant-table__create",
            onSubmit: (e) => e.preventDefault(),
            children: [
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Subject kind" }),
                /* @__PURE__ */ jsx(
                  "select",
                  {
                    value: subjectKind,
                    "data-testid": "privz-admin-grant__subject-kind",
                    "data-wired": "true",
                    onChange: (e) => {
                      setSubjectKind(e.target.value);
                      onDraftChange?.({ subjectKind: e.target.value, capability });
                    },
                    children: SUBJECT_KINDS.map((k) => /* @__PURE__ */ jsx("option", { value: k, children: k }, k))
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Subject id" }),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "text",
                    value: subjectId,
                    "data-testid": "privz-admin-grant__subject-id",
                    onChange: (e) => setSubjectId(e.target.value)
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Capability" }),
                /* @__PURE__ */ jsxs(
                  "select",
                  {
                    value: capability,
                    "data-testid": "privz-admin-grant__capability",
                    "data-wired": "true",
                    onChange: (e) => {
                      setCapability(e.target.value);
                      onDraftChange?.({ subjectKind, capability: e.target.value });
                    },
                    children: [
                      /* @__PURE__ */ jsx("option", { value: "", children: "Choose a capability\u2026" }),
                      capabilityKeys.map((k) => /* @__PURE__ */ jsx("option", { value: k, children: k }, k))
                    ]
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Effect" }),
                /* @__PURE__ */ jsxs(
                  "select",
                  {
                    value: effect2,
                    "data-testid": "privz-admin-grant__effect",
                    "data-wired": "true",
                    onChange: (e) => setEffect(e.target.value),
                    children: [
                      /* @__PURE__ */ jsx("option", { value: "allow", children: "allow" }),
                      /* @__PURE__ */ jsx("option", { value: "deny", children: "deny (beats every allow)" })
                    ]
                  }
                )
              ] }),
              /* @__PURE__ */ jsx(
                GuardedButton,
                {
                  availability: createAvailability,
                  testId: "privz-admin-grant__submit",
                  onClick: onCreate && ready ? () => {
                    onCreate({
                      subject: { subject_kind: subjectKind, subject_id: subjectId.trim() },
                      scope,
                      capability,
                      effect: effect2
                    });
                    setSubjectId("");
                  } : void 0,
                  children: "Grant"
                }
              )
            ]
          }
        )
      ]
    }
  );
}
function AuthorityFooter({ authority, testId }) {
  return /* @__PURE__ */ jsx("p", { className: "privz-admin__authority", "data-testid": testId, "data-authority": authority, children: authority === "server" ? "Resolved by the server. This is the answer that will be enforced." : "Resolved locally to explain and to preview. The server re-resolves and enforces on every request \u2014 this answer grants nothing and is never sent anywhere." });
}
function GroupMembership({
  personId,
  allGroups,
  memberOf,
  resolved,
  authority,
  explainAvailability = AVAILABLE,
  addAvailability = AVAILABLE,
  removeAvailability = AVAILABLE,
  onAdd,
  onRemove
}) {
  const memberIds = new Set(memberOf.map((g) => g.id));
  const nonMembers = allGroups.filter((g) => !memberIds.has(g.id));
  return /* @__PURE__ */ jsxs(
    DataPanel,
    {
      title: "Group membership",
      meta: `${personId} \xB7 in ${formatNumber(memberOf.length)} of ${formatNumber(allGroups.length)} groups`,
      testId: "privz-admin-group-membership",
      children: [
        /* @__PURE__ */ jsxs("section", { "aria-label": "Groups this person belongs to", children: [
          /* @__PURE__ */ jsx("h4", { className: "privz-admin__subhead", children: "Member of" }),
          memberOf.length === 0 ? /* @__PURE__ */ jsx(
            EmptyState,
            {
              title: "Not in any group",
              description: "This person holds only whatever is granted to them directly, or through their account."
            }
          ) : /* @__PURE__ */ jsx("ul", { className: "privz-admin__list", "data-testid": "privz-admin-membership__in", children: memberOf.map((g) => /* @__PURE__ */ jsxs(
            "li",
            {
              className: "privz-admin__list-row",
              "data-testid": `privz-admin-membership__in--${g.id}`,
              children: [
                /* @__PURE__ */ jsx("span", { children: g.display_name || g.key }),
                /* @__PURE__ */ jsx("code", { className: "privz-admin__key", children: g.key }),
                /* @__PURE__ */ jsx(
                  GuardedButton,
                  {
                    availability: removeAvailability,
                    tone: "danger",
                    testId: `privz-admin-membership__remove--${g.id}`,
                    onClick: onRemove ? () => onRemove(g.id) : void 0,
                    children: "Remove"
                  }
                )
              ]
            },
            g.id
          )) })
        ] }),
        /* @__PURE__ */ jsxs("section", { "aria-label": "Groups this person could join", children: [
          /* @__PURE__ */ jsx("h4", { className: "privz-admin__subhead", children: "Available groups" }),
          nonMembers.length === 0 ? /* @__PURE__ */ jsx("p", { className: "privz-admin__muted", "data-testid": "privz-admin-membership__none-left", children: "This person is already in every group on the account." }) : /* @__PURE__ */ jsx("ul", { className: "privz-admin__list", "data-testid": "privz-admin-membership__out", children: nonMembers.map((g) => /* @__PURE__ */ jsxs(
            "li",
            {
              className: "privz-admin__list-row",
              "data-testid": `privz-admin-membership__out--${g.id}`,
              children: [
                /* @__PURE__ */ jsx("span", { children: g.display_name || g.key }),
                /* @__PURE__ */ jsx(
                  GuardedButton,
                  {
                    availability: addAvailability,
                    testId: `privz-admin-membership__add--${g.id}`,
                    onClick: onAdd ? () => onAdd(g.id) : void 0,
                    children: "Add"
                  }
                )
              ]
            },
            g.id
          )) })
        ] }),
        /* @__PURE__ */ jsxs("section", { "aria-label": "Resolved capabilities", children: [
          /* @__PURE__ */ jsxs("h4", { className: "privz-admin__subhead", children: [
            "Resulting capabilities",
            resolved !== null ? /* @__PURE__ */ jsx(BadgePill, { tone: "neutral", size: "sm", children: formatNumber(resolved.length) }) : null
          ] }),
          /* @__PURE__ */ jsx(
            UnavailableNotice,
            {
              availability: explainAvailability,
              what: "The resolved union",
              testId: "privz-admin-membership__explain-unavailable"
            }
          ),
          resolved === null ? null : resolved.length === 0 ? /* @__PURE__ */ jsx(
            EmptyState,
            {
              title: "No capabilities on this scope",
              description: "Every allow this person would receive is cancelled by a deny, or there is nothing granted here at all. A deny always beats an allow."
            }
          ) : /* @__PURE__ */ jsx("ul", { className: "privz-admin__caps", "data-testid": "privz-admin-membership__resolved", children: resolved.map((r) => /* @__PURE__ */ jsxs("li", { "data-testid": `privz-admin-membership__cap--${r.capability}`, children: [
            /* @__PURE__ */ jsx("code", { className: "privz-admin__cap-key", children: r.capability }),
            /* @__PURE__ */ jsx(
              ProvenanceChip,
              {
                provenance: r.provenance,
                testId: `privz-admin-membership__prov--${r.capability}`
              }
            )
          ] }, r.capability)) }),
          /* @__PURE__ */ jsx(AuthorityFooter, { authority, testId: "privz-admin-membership__authority" })
        ] })
      ]
    }
  );
}
function GroupRoster({
  accountId,
  groups,
  memberCounts,
  selectedGroupId,
  createAvailability = AVAILABLE,
  archiveAvailability = AVAILABLE,
  onSelect,
  onCreate,
  onArchive
}) {
  const [key, setKey] = useState("");
  const [displayName, setDisplayName] = useState("");
  return /* @__PURE__ */ jsxs(
    DataPanel,
    {
      title: "Groups",
      meta: `account ${accountId} \xB7 ${formatNumber(groups.length)} groups`,
      testId: "privz-admin-group-roster",
      children: [
        groups.length === 0 ? /* @__PURE__ */ jsx(
          EmptyState,
          {
            title: "No groups on this account",
            description: "Groups are how one person carries several sets of capabilities at once. Create the first one below."
          }
        ) : /* @__PURE__ */ jsx("ul", { className: "privz-admin__list", "data-testid": "privz-admin-group-roster__list", children: groups.map((g) => /* @__PURE__ */ jsxs(
          "li",
          {
            className: "privz-admin__list-row",
            "data-testid": `privz-admin-group--${g.id}`,
            "data-selected": g.id === selectedGroupId ? "true" : "false",
            children: [
              /* @__PURE__ */ jsx(
                GuardedButton,
                {
                  availability: AVAILABLE,
                  testId: `privz-admin-group__select--${g.id}`,
                  onClick: onSelect ? () => onSelect(g.id) : void 0,
                  children: g.display_name || g.key
                }
              ),
              /* @__PURE__ */ jsx("code", { className: "privz-admin__key", children: g.key }),
              memberCounts?.[g.id] !== void 0 ? /* @__PURE__ */ jsxs(BadgePill, { tone: "info", size: "sm", children: [
                formatNumber(memberCounts[g.id]),
                " members"
              ] }) : null,
              /* @__PURE__ */ jsx(
                GuardedButton,
                {
                  availability: archiveAvailability,
                  tone: "danger",
                  testId: `privz-admin-group__archive--${g.id}`,
                  onClick: onArchive ? () => onArchive(g.id) : void 0,
                  children: "Archive"
                }
              )
            ]
          },
          g.id
        )) }),
        /* @__PURE__ */ jsxs(
          "form",
          {
            className: "privz-admin__form",
            "data-testid": "privz-admin-group-roster__create",
            onSubmit: (e) => {
              e.preventDefault();
              if (onCreate && key.trim().length > 0) {
                onCreate({ key: key.trim(), display_name: displayName.trim() });
                setKey("");
                setDisplayName("");
              }
            },
            children: [
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Group key" }),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "text",
                    value: key,
                    "data-testid": "privz-admin-group-roster__key",
                    onChange: (e) => setKey(e.target.value)
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Display name" }),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "text",
                    value: displayName,
                    "data-testid": "privz-admin-group-roster__name",
                    onChange: (e) => setDisplayName(e.target.value)
                  }
                )
              ] }),
              /* @__PURE__ */ jsx(
                GuardedButton,
                {
                  availability: createAvailability,
                  testId: "privz-admin-group-roster__submit",
                  onClick: onCreate && key.trim().length > 0 ? () => {
                    onCreate({ key: key.trim(), display_name: displayName.trim() });
                    setKey("");
                    setDisplayName("");
                  } : void 0,
                  children: "Create group"
                }
              )
            ]
          }
        )
      ]
    }
  );
}
function PolicySimulator({
  query,
  onQueryChange,
  serverResult,
  localPreview,
  runAvailability = AVAILABLE,
  previewAvailability = AVAILABLE,
  onRun
}) {
  const [touched, setTouched] = useState(false);
  return /* @__PURE__ */ jsxs(DataPanel, { title: "Policy simulator", testId: "privz-admin-policy-simulator", children: [
    /* @__PURE__ */ jsxs("form", { className: "privz-admin__form", onSubmit: (e) => e.preventDefault(), children: [
      /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
        /* @__PURE__ */ jsx("span", { children: "Which bundles hold\u2026" }),
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "text",
            value: query,
            "data-testid": "privz-admin-simulator__query",
            placeholder: "a capability key, optionally prefixed who-can:",
            onChange: (e) => {
              setTouched(true);
              onQueryChange(e.target.value);
            }
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        GuardedButton,
        {
          availability: runAvailability,
          testId: "privz-admin-simulator__run",
          onClick: onRun && query.trim().length > 0 ? onRun : void 0,
          children: "Ask the server"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs("section", { "aria-label": "Server answer", children: [
      /* @__PURE__ */ jsx("h4", { className: "privz-admin__subhead", children: "Committed answer" }),
      serverResult === null ? /* @__PURE__ */ jsx(
        EmptyState,
        {
          title: touched ? "Not asked yet" : "Nothing simulated",
          description: "The server has not been asked about this query. Only its answer is binding."
        }
      ) : /* @__PURE__ */ jsxs("div", { "data-testid": "privz-admin-simulator__server", children: [
        /* @__PURE__ */ jsxs("p", { children: [
          "Allowed by",
          " ",
          /* @__PURE__ */ jsx(BadgePill, { tone: "success", size: "sm", children: formatNumber(serverResult.allowed.length) }),
          " ",
          "bundles, denied by",
          " ",
          /* @__PURE__ */ jsx(BadgePill, { tone: "danger", size: "sm", children: formatNumber(serverResult.denied.length) }),
          "."
        ] }),
        /* @__PURE__ */ jsx("ul", { className: "privz-admin__caps", children: serverResult.allowed.map((b) => /* @__PURE__ */ jsx("li", { "data-testid": `privz-admin-simulator__allowed--${b}`, children: /* @__PURE__ */ jsx("code", { className: "privz-admin__key", children: b }) }, b)) })
      ] }),
      /* @__PURE__ */ jsx(AuthorityFooter, { authority: "server", testId: "privz-admin-simulator__server-authority" })
    ] }),
    /* @__PURE__ */ jsxs("section", { "aria-label": "Local preview", children: [
      /* @__PURE__ */ jsx("h4", { className: "privz-admin__subhead", children: "Local preview of a membership change" }),
      /* @__PURE__ */ jsx(
        UnavailableNotice,
        {
          availability: previewAvailability,
          what: "The local preview",
          testId: "privz-admin-simulator__preview-unavailable"
        }
      ),
      localPreview === null ? null : /* @__PURE__ */ jsxs("div", { "data-testid": "privz-admin-simulator__preview", children: [
        /* @__PURE__ */ jsxs("p", { "data-testid": "privz-admin-simulator__preview-summary", children: [
          "Would gain ",
          formatNumber(localPreview.gained.length),
          ", lose",
          " ",
          formatNumber(localPreview.lost.length),
          ", keep",
          " ",
          formatNumber(localPreview.retained_count),
          ". Projected role",
          " ",
          /* @__PURE__ */ jsx("code", { children: localPreview.bundle_role_before ?? "none" }),
          " \u2192",
          " ",
          /* @__PURE__ */ jsx("code", { children: localPreview.bundle_role_after ?? "none" }),
          "."
        ] }),
        /* @__PURE__ */ jsxs("ul", { className: "privz-admin__caps", children: [
          localPreview.gained.map((g) => /* @__PURE__ */ jsx("li", { "data-testid": `privz-admin-simulator__gain--${g.capability}`, children: /* @__PURE__ */ jsxs("code", { className: "privz-admin__cap-key", children: [
            "+ ",
            g.capability
          ] }) }, g.capability)),
          localPreview.lost.map((c) => /* @__PURE__ */ jsx("li", { "data-testid": `privz-admin-simulator__loss--${c}`, children: /* @__PURE__ */ jsxs("code", { className: "privz-admin__cap-key", children: [
            "\u2212 ",
            c
          ] }) }, c))
        ] }),
        /* @__PURE__ */ jsx(
          AuthorityFooter,
          {
            authority: "local-preview",
            testId: "privz-admin-simulator__preview-authority"
          }
        )
      ] })
    ] })
  ] });
}
function diff(a, b) {
  const sa = new Set(a.map((x) => x.capability));
  const sb = new Set(b.map((x) => x.capability));
  return {
    onlyServer: [...sa].filter((c) => !sb.has(c)).sort(),
    onlyLocal: [...sb].filter((c) => !sa.has(c)).sort()
  };
}
function PrincipalInspector({
  personId,
  scope,
  serverResolved,
  bundleRole,
  localResolved,
  explainAvailability = AVAILABLE
}) {
  const drift = serverResolved !== null && localResolved !== null ? diff(serverResolved, localResolved) : null;
  const hasDrift = drift !== null && (drift.onlyServer.length > 0 || drift.onlyLocal.length > 0);
  return /* @__PURE__ */ jsxs(
    DataPanel,
    {
      title: "Principal inspector",
      meta: `${personId} on ${scope.scope_kind}:${scope.scope_id}`,
      testId: "privz-admin-principal-inspector",
      children: [
        bundleRole !== void 0 && bundleRole !== null ? /* @__PURE__ */ jsxs("p", { className: "privz-admin__muted", "data-testid": "privz-admin-inspector__bundle-role", children: [
          "Projected role on this scope: ",
          /* @__PURE__ */ jsx("strong", { children: bundleRole })
        ] }) : null,
        /* @__PURE__ */ jsxs("section", { "aria-label": "Server-resolved capabilities", children: [
          /* @__PURE__ */ jsxs("h4", { className: "privz-admin__subhead", children: [
            "Enforced by the server",
            serverResolved !== null ? /* @__PURE__ */ jsx(BadgePill, { tone: "success", size: "sm", children: formatNumber(serverResolved.length) }) : null
          ] }),
          serverResolved === null ? /* @__PURE__ */ jsx(LoadingState, { label: "Asking the server what this principal holds" }) : serverResolved.length === 0 ? /* @__PURE__ */ jsx(
            EmptyState,
            {
              title: "Holds nothing on this scope",
              description: "Either nothing reaches this principal here, or every allow is cancelled by a deny."
            }
          ) : /* @__PURE__ */ jsx("ul", { className: "privz-admin__caps", "data-testid": "privz-admin-inspector__server", children: serverResolved.map((r) => /* @__PURE__ */ jsxs("li", { "data-testid": `privz-admin-inspector__server-cap--${r.capability}`, children: [
            /* @__PURE__ */ jsx("code", { className: "privz-admin__cap-key", children: r.capability }),
            /* @__PURE__ */ jsx(
              ProvenanceChip,
              {
                provenance: r.provenance,
                testId: `privz-admin-inspector__server-prov--${r.capability}`
              }
            )
          ] }, r.capability)) }),
          /* @__PURE__ */ jsx(AuthorityFooter, { authority: "server", testId: "privz-admin-inspector__server-authority" })
        ] }),
        /* @__PURE__ */ jsxs("section", { "aria-label": "Locally explained capabilities", children: [
          /* @__PURE__ */ jsx("h4", { className: "privz-admin__subhead", children: "Explained locally" }),
          /* @__PURE__ */ jsx(
            UnavailableNotice,
            {
              availability: explainAvailability,
              what: "Local explanation",
              testId: "privz-admin-inspector__explain-unavailable"
            }
          ),
          localResolved === null ? null : /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx("ul", { className: "privz-admin__caps", "data-testid": "privz-admin-inspector__local", children: localResolved.map((r) => /* @__PURE__ */ jsxs("li", { "data-testid": `privz-admin-inspector__local-cap--${r.capability}`, children: [
              /* @__PURE__ */ jsx("code", { className: "privz-admin__cap-key", children: r.capability }),
              /* @__PURE__ */ jsx(
                ProvenanceChip,
                {
                  provenance: r.provenance,
                  testId: `privz-admin-inspector__local-prov--${r.capability}`
                }
              )
            ] }, r.capability)) }),
            /* @__PURE__ */ jsx(
              AuthorityFooter,
              {
                authority: "local-preview",
                testId: "privz-admin-inspector__local-authority"
              }
            )
          ] })
        ] }),
        hasDrift ? /* @__PURE__ */ jsxs("p", { className: "privz-admin__drift", role: "alert", "data-testid": "privz-admin-inspector__drift", children: [
          /* @__PURE__ */ jsx("strong", { children: "These two answers disagree." }),
          " The server is authoritative. Only the server:",
          " ",
          /* @__PURE__ */ jsx("code", { children: drift.onlyServer.length > 0 ? drift.onlyServer.join(", ") : "none" }),
          ". Only local: ",
          /* @__PURE__ */ jsx("code", { children: drift.onlyLocal.length > 0 ? drift.onlyLocal.join(", ") : "none" }),
          ". A disagreement means the snapshot this console was handed is stale or incomplete \u2014 reload before acting on either list."
        ] }) : null
      ]
    }
  );
}
function SodPairList({
  pairs,
  held,
  conflicts,
  authority = "server",
  capabilityKeys = [],
  createAvailability = AVAILABLE,
  removeAvailability = AVAILABLE,
  onCreate,
  onRemove
}) {
  const [capA, setCapA] = useState("");
  const [capB, setCapB] = useState("");
  const conflicting = new Set(conflicts.map((c) => `${c.capability_a}::${c.capability_b}`));
  const ready = capA.length > 0 && capB.length > 0 && capA !== capB;
  return /* @__PURE__ */ jsxs(
    DataPanel,
    {
      title: "Separation of duties",
      meta: `${formatNumber(pairs.length)} pairs \xB7 ${formatNumber(conflicts.length)} violated`,
      testId: "privz-admin-sod",
      children: [
        pairs.length === 0 ? /* @__PURE__ */ jsx(
          EmptyState,
          {
            title: "No pairs declared",
            description: "Nothing is being checked. A pair names two capabilities one principal should never hold at once."
          }
        ) : /* @__PURE__ */ jsx("ul", { className: "privz-admin__list", "data-testid": "privz-admin-sod__pairs", children: pairs.map((p) => {
          const id = `${p.capability_a}::${p.capability_b}`;
          const violated = conflicting.has(id);
          return /* @__PURE__ */ jsxs(
            "li",
            {
              className: "privz-admin__list-row",
              "data-testid": `privz-admin-sod-pair--${id}`,
              "data-violated": violated ? "true" : "false",
              children: [
                /* @__PURE__ */ jsx("code", { className: "privz-admin__cap-key", children: p.capability_a }),
                /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: "\xD7" }),
                /* @__PURE__ */ jsx("code", { className: "privz-admin__cap-key", children: p.capability_b }),
                /* @__PURE__ */ jsx(BadgePill, { tone: violated ? "danger" : "success", size: "sm", children: violated ? "held together" : "clear" }),
                /* @__PURE__ */ jsx(
                  GuardedButton,
                  {
                    availability: removeAvailability,
                    tone: "danger",
                    testId: `privz-admin-sod-pair__remove--${id}`,
                    onClick: onRemove ? () => onRemove(p) : void 0,
                    children: "Retract"
                  }
                )
              ]
            },
            id
          );
        }) }),
        /* @__PURE__ */ jsxs(
          "form",
          {
            className: "privz-admin__form",
            "data-testid": "privz-admin-sod__define",
            onSubmit: (e) => e.preventDefault(),
            children: [
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Capability A" }),
                /* @__PURE__ */ jsxs(
                  "select",
                  {
                    value: capA,
                    "data-testid": "privz-admin-sod__cap-a",
                    "data-wired": "true",
                    onChange: (e) => setCapA(e.target.value),
                    children: [
                      /* @__PURE__ */ jsx("option", { value: "", children: "Choose a capability\u2026" }),
                      capabilityKeys.map((k) => /* @__PURE__ */ jsx("option", { value: k, children: k }, k))
                    ]
                  }
                )
              ] }),
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Capability B" }),
                /* @__PURE__ */ jsxs(
                  "select",
                  {
                    value: capB,
                    "data-testid": "privz-admin-sod__cap-b",
                    "data-wired": "true",
                    onChange: (e) => setCapB(e.target.value),
                    children: [
                      /* @__PURE__ */ jsx("option", { value: "", children: "Choose a capability\u2026" }),
                      capabilityKeys.map((k) => /* @__PURE__ */ jsx("option", { value: k, children: k }, k))
                    ]
                  }
                )
              ] }),
              /* @__PURE__ */ jsx(
                GuardedButton,
                {
                  availability: createAvailability,
                  testId: "privz-admin-sod__submit",
                  onClick: onCreate && ready ? () => {
                    onCreate({ capability_a: capA, capability_b: capB });
                    setCapA("");
                    setCapB("");
                  } : void 0,
                  children: "Declare a pair"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs("p", { className: "privz-admin__muted", "data-testid": "privz-admin-sod__held-count", children: [
          "Checked against ",
          formatNumber(held.length),
          " held capabilities.",
          " ",
          authority === "server" ? "The pairs above were fetched from the server." : "The pairs above were supplied by this deployment, not fetched \u2014 the server may know a different set."
        ] }),
        /* @__PURE__ */ jsx(AuthorityFooter, { authority: "local-preview", testId: "privz-admin-sod__authority" })
      ]
    }
  );
}

// src/resolution.ts
function capabilitiesOfSubject(subject, bundles) {
  const out = /* @__PURE__ */ new Set();
  for (const g of subject.grants ?? []) {
    if (g.capability) out.add(g.capability);
    if (g.bundle_id) {
      const b = bundles.find((x) => x.id === g.bundle_id);
      for (const c of b?.capabilities ?? []) out.add(c.capability);
    }
  }
  return out;
}
function attributeGroups(snapshot, groupLabels) {
  const subjects = snapshot.subjects ?? [];
  const bundles = snapshot.bundles ?? [];
  const contributors = /* @__PURE__ */ new Map();
  subjects.forEach((s, idx) => {
    for (const cap of capabilitiesOfSubject(s, bundles)) {
      const list = contributors.get(cap);
      if (list) list.push(idx);
      else contributors.set(cap, [idx]);
    }
  });
  const groupIndexOf = /* @__PURE__ */ new Map();
  let seen = 0;
  subjects.forEach((s, idx) => {
    if (s.kind === "group") {
      groupIndexOf.set(idx, seen);
      seen += 1;
    }
  });
  const out = /* @__PURE__ */ new Map();
  for (const [cap, idxs] of contributors) {
    if (idxs.length !== 1) continue;
    const only = idxs[0];
    const gi = groupIndexOf.get(only);
    if (gi === void 0) continue;
    const label = groupLabels[gi];
    if (label !== void 0) out.set(cap, label);
  }
  return out;
}
function resolveLocally(privz, snapshot, scope, nowUnix, groupLabels = []) {
  const answer = privz.resolve(snapshot, scope, nowUnix);
  const groups = attributeGroups(snapshot, groupLabels);
  return {
    capabilities: toResolvedCapabilities(
      answer.entries,
      (_source, capability) => groups.get(capability)
    ),
    bundleRole: answer.bundle_role,
    snapshotVersion: answer.snapshot_version
  };
}
function withoutGroupAt(snapshot, groupOrdinal) {
  const subjects = snapshot.subjects ?? [];
  let seen = 0;
  const kept = subjects.filter((s) => {
    if (s.kind !== "group") return true;
    const keep = seen !== groupOrdinal;
    seen += 1;
    return keep;
  });
  return { ...snapshot, subjects: kept };
}
function withGroup(snapshot, grants) {
  return {
    ...snapshot,
    subjects: [...snapshot.subjects ?? [], { kind: "group", grants }]
  };
}
var TABS = [
  { value: "membership", label: "Membership" },
  { value: "grid", label: "Capability grid" },
  { value: "grants", label: "Grants" },
  { value: "bundles", label: "Bundles" },
  { value: "inspector", label: "Inspector" },
  { value: "policy", label: "Policy" },
  { value: "elevation", label: "Elevation" },
  { value: "reviews", label: "Access reviews" },
  { value: "sod", label: "Duties" }
];
var EMPTY = {
  capabilities: [],
  bundles: [],
  groups: [],
  membersByGroup: {},
  grants: [],
  resolved: null,
  matrix: null,
  elevation: null,
  campaigns: [],
  sodPairs: null
};
function PrivilegesAdminPage() {
  const { client, deps, snapshotSource, privz, explainAvailability, nowUnix } = usePrivilegesAdmin();
  const { announce } = useLiveAnnouncer();
  const [tab, setTab] = useState("membership");
  const [data, setData] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [personId, setPersonId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState(void 0);
  const [selectedBundleId, setSelectedBundleId] = useState(void 0);
  const [query, setQuery] = useState("");
  const [policy, setPolicy] = useState(null);
  const [draft, setDraft] = useState({ subjectKind: "user", capability: "" });
  const [snapshot, setSnapshot] = useState(null);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useEffect(() => {
    let live = true;
    const load = async () => {
      const [capabilities, bundles, groups, grants, resolved] = await Promise.all([
        client.listCapabilities(),
        client.listBundles(),
        client.listGroups(deps.accountId),
        client.listGrantsForScope(deps.scope),
        client.resolve(deps.scope)
      ]);
      const membersByGroup = {};
      await Promise.all(
        groups.map(async (g) => {
          const members = await client.listGroupMembers(g.id);
          membersByGroup[g.id] = members.map((m) => m.person_id);
        })
      );
      const optional = await Promise.allSettled([
        client.rolesMatrix(),
        client.elevationBoard(),
        client.listAccessReviewCampaigns(),
        client.listSodPairs()
      ]);
      if (!live) return;
      setData({
        capabilities,
        bundles,
        groups,
        membersByGroup,
        grants,
        resolved,
        matrix: optional[0].status === "fulfilled" ? optional[0].value : null,
        elevation: optional[1].status === "fulfilled" ? optional[1].value : null,
        campaigns: optional[2].status === "fulfilled" ? optional[2].value : [],
        sodPairs: optional[3].status === "fulfilled" ? optional[3].value : null
      });
      announce(`Loaded ${capabilities.length} capabilities and ${groups.length} groups.`);
    };
    load().catch((e) => {
      if (live) setError(e instanceof Error ? e : new Error(String(e)));
    });
    return () => {
      live = false;
    };
  }, [client, deps.accountId, deps.scope, announce, reloadKey]);
  const stableScope = useMemo(
    () => ({ scope_kind: deps.scope.scope_kind, scope_id: deps.scope.scope_id }),
    [deps.scope.scope_kind, deps.scope.scope_id]
  );
  useEffect(() => {
    let live = true;
    if (snapshotSource === void 0 || personId.length === 0) {
      setSnapshot(null);
      return;
    }
    snapshotSource({ personId, scope: stableScope }).then((s) => {
      if (live) setSnapshot(s);
    }).catch(() => {
      if (live) setSnapshot(null);
    });
    return () => {
      live = false;
    };
  }, [snapshotSource, stableScope, personId]);
  const vocabulary = useMemo(
    () => buildVocabulary(data.capabilities, deps.vocabulary),
    [data.capabilities, deps.vocabulary]
  );
  const memberOf = useMemo(
    () => data.groups.filter((g) => (data.membersByGroup[g.id] ?? []).includes(personId)),
    [data.groups, data.membersByGroup, personId]
  );
  const local = useMemo(() => {
    if (privz === null || snapshot === null) return null;
    try {
      return resolveLocally(
        privz,
        snapshot,
        { kind: deps.scope.scope_kind, id: deps.scope.scope_id },
        nowUnix(),
        memberOf.map((g) => g.display_name || g.key)
      );
    } catch {
      return null;
    }
  }, [privz, snapshot, deps.scope, nowUnix, memberOf]);
  const serverResolved = useMemo(
    () => data.resolved === null ? null : toResolvedCapabilities(data.resolved.entries),
    [data.resolved]
  );
  const heldCapabilities = useMemo(
    () => (data.resolved?.entries ?? []).map((e) => e.capability),
    [data.resolved]
  );
  const sodPairs = useMemo(
    () => data.sodPairs ?? deps.sodPairs ?? [],
    [data.sodPairs, deps.sodPairs]
  );
  const sodAuthority = data.sodPairs !== null ? "server" : "host-supplied";
  const sodConflicts = useMemo(() => {
    if (privz === null || sodPairs.length === 0) return [];
    try {
      return privz.sodConflicts([...sodPairs], heldCapabilities);
    } catch {
      return [];
    }
  }, [privz, sodPairs, heldCapabilities]);
  const grantCeiling = useMemo(() => {
    if (privz === null || draft.capability.length === 0) return AVAILABLE;
    try {
      return fromCanGrant(
        privz.canGrant({
          grantor_role: data.resolved?.bundle_role ?? "none",
          grantee_role: draft.subjectKind === "account" ? "owner" : "editor"
        })
      );
    } catch {
      return AVAILABLE;
    }
  }, [privz, draft, data.resolved]);
  const manage = requiresCapability(deps.permissions, "member.manage", "changing access here");
  if (error !== null) throw error;
  return /* @__PURE__ */ jsx(
    ViewContainer,
    {
      resetKeys: [reloadKey],
      onReset: reload,
      fallback: (err, reset) => /* @__PURE__ */ jsx(
        DefaultErrorFallback,
        {
          error: err,
          reset,
          title: "Access administration could not load. Nothing was changed \u2014 every read on this page comes from the server, so retrying is safe."
        }
      ),
      children: /* @__PURE__ */ jsxs(
        "div",
        {
          className: "privz-admin",
          "data-testid": "privz-admin",
          ...withConformance(WEB_PRIVILEGES_ADMIN_LIB, LIB_VERSION),
          children: [
            /* @__PURE__ */ jsxs("header", { className: "privz-admin__header", children: [
              /* @__PURE__ */ jsx("h2", { className: "privz-admin__title", children: "Access administration" }),
              /* @__PURE__ */ jsxs("label", { className: "privz-admin__field", children: [
                /* @__PURE__ */ jsx("span", { children: "Person" }),
                /* @__PURE__ */ jsx(
                  "input",
                  {
                    type: "text",
                    value: personId,
                    "data-testid": "privz-admin__person",
                    onChange: (e) => setPersonId(e.target.value)
                  }
                )
              ] })
            ] }),
            /* @__PURE__ */ jsx(
              Tabs,
              {
                tabs: TABS.map((t) => ({ value: t.value, label: t.label })),
                value: tab,
                onChange: (v) => setTab(v),
                testId: "privz-admin__tabs"
              }
            ),
            /* @__PURE__ */ jsxs(TabPanels, { value: tab, children: [
              /* @__PURE__ */ jsx(TabPanel, { value: "membership", children: /* @__PURE__ */ jsx(
                GroupMembership,
                {
                  personId,
                  allGroups: data.groups,
                  memberOf,
                  resolved: local?.capabilities ?? null,
                  authority: "local-preview",
                  explainAvailability,
                  addAvailability: manage,
                  removeAvailability: manage,
                  onAdd: (gid) => {
                    void client.addGroupMember(gid, personId).then(reload);
                  },
                  onRemove: (gid) => {
                    void client.removeGroupMember(gid, personId).then(reload);
                  }
                }
              ) }),
              /* @__PURE__ */ jsx(TabPanel, { value: "grid", children: data.matrix === null ? null : /* @__PURE__ */ jsx(
                CapabilityGrid,
                {
                  matrix: data.matrix,
                  vocabulary: deps.vocabulary,
                  editAvailability: manage,
                  onSaveBundle: (id, atoms) => {
                    void client.editRoleBundle(id, atoms).then(reload);
                  }
                }
              ) }),
              /* @__PURE__ */ jsx(TabPanel, { value: "grants", children: /* @__PURE__ */ jsx(
                GrantTable,
                {
                  scope: deps.scope,
                  grants: data.grants,
                  capabilityKeys: vocabulary.map((v) => v.key),
                  createAvailability: manage.disabled ? manage : grantCeiling,
                  revokeAvailability: manage,
                  onDraftChange: setDraft,
                  onCreate: (body) => {
                    void client.createGrant(body).then(reload);
                  },
                  onRevoke: (id) => {
                    void client.revokeGrant(id).then(reload);
                  }
                }
              ) }),
              /* @__PURE__ */ jsxs(TabPanel, { value: "bundles", children: [
                /* @__PURE__ */ jsx(
                  BundleEditor,
                  {
                    bundles: data.bundles,
                    vocabulary,
                    capabilitiesOf: {},
                    selectedBundleId,
                    defineAvailability: manage,
                    setCapAvailability: manage,
                    onSelect: setSelectedBundleId,
                    onDefine: (body) => {
                      void client.defineBundle(body).then(reload);
                    },
                    onSetCapability: (id, cap, effect2) => {
                      void client.setBundleCapability(id, cap, effect2).then(reload);
                    },
                    onRemoveCapability: (id, cap) => {
                      void client.removeBundleCapability(id, cap).then(reload);
                    }
                  }
                ),
                /* @__PURE__ */ jsx(
                  GroupRoster,
                  {
                    accountId: deps.accountId,
                    groups: data.groups,
                    selectedGroupId,
                    createAvailability: manage,
                    archiveAvailability: manage,
                    onSelect: setSelectedGroupId,
                    onCreate: (body) => {
                      void client.createGroup({ account_id: deps.accountId, ...body }).then(reload);
                    },
                    onArchive: (id) => {
                      void client.archiveGroup(id).then(reload);
                    }
                  }
                )
              ] }),
              /* @__PURE__ */ jsx(TabPanel, { value: "inspector", children: /* @__PURE__ */ jsx(
                PrincipalInspector,
                {
                  personId,
                  scope: deps.scope,
                  serverResolved,
                  bundleRole: data.resolved?.bundle_role ?? null,
                  localResolved: local?.capabilities ?? null,
                  explainAvailability
                }
              ) }),
              /* @__PURE__ */ jsx(TabPanel, { value: "policy", children: /* @__PURE__ */ jsx(
                PolicySimulator,
                {
                  query,
                  onQueryChange: setQuery,
                  serverResult: policy,
                  localPreview: null,
                  previewAvailability: explainAvailability,
                  onRun: () => {
                    void client.simulatePolicy(query).then(setPolicy);
                  }
                }
              ) }),
              /* @__PURE__ */ jsx(TabPanel, { value: "elevation", children: data.elevation === null ? null : /* @__PURE__ */ jsx(
                ElevationBoard,
                {
                  board: data.elevation,
                  capabilityKeys: vocabulary.map((v) => v.key),
                  approveAvailability: manage,
                  requestAvailability: availabilityOf(OPS.elevationRequest, manage),
                  onApprove: (id) => {
                    void client.approveElevation(id).then(reload);
                  },
                  onRequest: (body) => {
                    void client.requestElevation(body).then(reload);
                  }
                }
              ) }),
              /* @__PURE__ */ jsx(TabPanel, { value: "reviews", children: /* @__PURE__ */ jsx(
                AccessReviewCampaign,
                {
                  campaigns: data.campaigns,
                  createAvailability: availabilityOf(OPS.accessReviewCreate, manage),
                  decideAvailability: availabilityOf(OPS.accessReviewDecide, manage),
                  closeAvailability: availabilityOf(OPS.accessReviewClose, manage),
                  onCreate: (body) => {
                    void client.createAccessReviewCampaign(body).then(reload);
                  },
                  onDecide: (campaignId, body) => {
                    void client.decideAccessReviewItem(campaignId, body).then(reload);
                  },
                  onClose: (campaignId) => {
                    void client.closeAccessReviewCampaign(campaignId).then(reload);
                  }
                }
              ) }),
              /* @__PURE__ */ jsx(TabPanel, { value: "sod", children: /* @__PURE__ */ jsx(
                SodPairList,
                {
                  pairs: sodPairs,
                  held: heldCapabilities,
                  conflicts: sodConflicts,
                  authority: sodAuthority,
                  capabilityKeys: vocabulary.map((v) => v.key),
                  createAvailability: availabilityOf(OPS.sodPairCreate, manage),
                  removeAvailability: availabilityOf(OPS.sodPairRemove, manage),
                  onCreate: (pair) => {
                    void client.createSodPair(pair).then(reload);
                  },
                  onRemove: (pair) => {
                    void client.removeSodPair(pair).then(reload);
                  }
                }
              ) })
            ] })
          ]
        }
      )
    }
  );
}
var PRIVILEGES_ADMIN_SEAT_PATH = "/ops/staff/roles";
var PRIVILEGES_ADMIN_MODULE_ID = "privileges-admin";
var PRIVILEGES_ADMIN_NAV_ID = "privileges-admin-access";
var PRIVILEGES_ADMIN_CAPABILITY = "ops.admin.read";
var LABEL = "console.privileges.label";
var NAV_LABEL = "console.privileges.nav.access";
var PrivilegesIcon = () => null;
function screen(paths, deps) {
  assertGenericIdentifiers({
    "module identifiers": [
      PRIVILEGES_ADMIN_MODULE_ID,
      PRIVILEGES_ADMIN_NAV_ID,
      PRIVILEGES_ADMIN_CAPABILITY,
      LABEL,
      NAV_LABEL
    ],
    "route paths": paths,
    "operation names": ALL_OPS.map((o) => o.op)
  });
  assertGenericVocabulary(deps.vocabulary ?? {});
}
function createPrivilegesAdminElement(deps) {
  function PrivilegesAdminRoute() {
    return /* @__PURE__ */ jsx(PrivilegesAdminProvider, { deps, children: /* @__PURE__ */ jsx(PrivilegesAdminPage, {}) });
  }
  PrivilegesAdminRoute.displayName = "PrivilegesAdminRoute";
  return PrivilegesAdminRoute;
}
function fillPrivilegesAdminSeat(persona, deps, seatPath = PRIVILEGES_ADMIN_SEAT_PATH) {
  screen([seatPath], deps);
  const element = createPrivilegesAdminElement(deps);
  let filled = false;
  const routes = persona.routes.map((r) => {
    if (r.path !== seatPath) return r;
    filled = true;
    return { ...r, element };
  });
  if (!filled) {
    throw new Error(
      `@norkron/web-privileges-admin: module "${persona.id}" declares no route at "${seatPath}", so there is no seat to fill. Pass the persona that owns it, or use createPrivilegesAdminModule() to mount on a path of your own.`
    );
  }
  return { ...persona, routes };
}
function createPrivilegesAdminModule(deps, options = {}) {
  const path = options.path ?? PRIVILEGES_ADMIN_SEAT_PATH;
  screen([path], deps);
  return {
    id: PRIVILEGES_ADMIN_MODULE_ID,
    label: LABEL,
    icon: PrivilegesIcon,
    requiredCapability: options.requiredCapability ?? PRIVILEGES_ADMIN_CAPABILITY,
    routes: [{ path, element: createPrivilegesAdminElement(deps) }],
    navItems: [{ id: PRIVILEGES_ADMIN_NAV_ID, label: NAV_LABEL, to: path }]
  };
}

// src/index.ts
try {
  registerLazy(
    WEB_PRIVILEGES_ADMIN_LIB,
    () => selfCheck
  );
} catch {
}

export { ALL_OPS, AVAILABLE, AccessReviewCampaign, AuthorityFooter, BundleEditor, CONSOLE_PROFILE, CapabilityGrid, ContractViolationError, DELIBERATELY_EXCLUDED_OPS, ElevationBoard, GenericityViolationError, GrantTable, GroupMembership, GroupRoster, GuardedButton, LIB_VERSION, MOUNT_OP_NAMES, MissingProviderError, NO_CELLS, OPS, PARAM_DENYLIST, PRIVILEGES_ADMIN_CAPABILITY, PRIVILEGES_ADMIN_MODULE_ID, PRIVILEGES_ADMIN_NAV_ID, PRIVILEGES_ADMIN_SEAT_PATH, PRIVZ_ADMIN_ERRORS, PRODUCT_TOKENS, PRODUCT_TOKENS_AMBIGUOUS, PRODUCT_TOKENS_STRICT, PolicySimulator, PrincipalInspector, PrivilegesAdminPage, PrivilegesAdminProvider, ProvenanceChip, REQUIRED_TOKENS, RESPONSIVE_CONTRACT, SERVED_OP_NAMES, SodPairList, UnavailableNotice, UnservedOperationError, VerdictLeakError, WEB_PRIVILEGES_ADMIN_LIB, assertGenericIdentifiers, assertGenericVocabulary, assertNoVerdict, assertServed, attributeGroups, availabilityOf, buildGridAxes, buildVocabulary, buildVocabularyFromAtoms, bundleStateKeyPrefix, cellCoords, cellError, cellIndex, cellPending, cellStateKey, cellTestId, cellValue, createPrivilegesAdminElement, createPrivilegesAdminModule, createPrivilegesClient, deriveGroup, failedKeys, fillPrivilegesAdminSeat, findInertControls, findProductTokens, fromCanGrant, groupVocabulary, heldIndex, isDenylistException, matrixSignature, optimisticReducer, parseProvenance, parseResponse, pendingKeys, requiresCapability, resolveLocally, selfCheck, splitCellStateKey, toResolvedCapabilities, unavailable, unsupported, useOptimisticCells, usePrivilegesAdmin, withGroup, withoutGroupAt };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map